import { getBrandTheme } from "./styles/brandTheme";
import { supabase } from "./lib/supabase";
import AuthScreen from "./components/AuthScreen";
import OnboardingFlow from "./components/onboarding/OnboardingFlow";
import PendingUpdatePrompt from "./components/onboarding/PendingUpdatePrompt";
import PasswordResetScreen from "./components/PasswordResetScreen";
import posthog from "./lib/posthog";
import { calculateNutritionTargets } from "./utils/nutritionCalc";

import HomeTab from "./tabs/home/HomeTab";
import PlanTab from "./tabs/plan/PlanTab.jsx";
import RecipesTab from "./tabs/recipes/RecipesTab";
import ShoppingTab from "./tabs/shopping/ShoppingTab";

import SettingsModal from "./tabs/settings/SettingsModal";
import FullWeekView from "./tabs/plan/FullWeekView";

import { programmeLibrary } from "./data/programmes";
import { recipeFilters, allRecipeFilters } from "./data/recipeFilters";
import { days } from "./data/days";
// defaultRecipes kept on disk as backup — no longer imported (migrated to Supabase)

import { groupShoppingItems } from "./utils/groupShoppingItems";
import { mergeShoppingIngredients, applyIngredientMultiplier } from "./utils/mergeShoppingIngredients";
import {
  getPlanCalendarWeekStart,
  getPlanCalendarDays,
  formatWeekBeginning,
  getDateKey
} from "./utils/planDates";
import {
  handleTouchStart as handleHomeTouchStart,
  handleTouchMove as handleHomeTouchMove,
  handleTouchEnd as handleHomeTouchEnd,
  handleMouseDown as handleHomeMouseDown,
  handleMouseMove as handleHomeMouseMove,
  handleMouseUp as handleHomeMouseUp
} from "./utils/homeDayModalDrag";
import {
  calculateStreakFromCompletedMeals,
  isDateCompletedFromMeals,
  getProfileCalendarDateKey,
  changeProfileCalendarMonth,
  getMealTimePickerState
} from "./utils/profileCalendar";
import {
  getProgrammeWeekCount,
  getCurrentProgrammeWeek
} from "./utils/programmeHelpers";
import {
  getMealsForDayInFixedOrder
} from "./utils/homeMeals";
import { getSettingsPageTitle } from "./utils/settingsHelpers";
import {
  toggleFavouriteRecipe,
  buildRecipeFromForm,
  dbRecipeToApp,
  appRecipeToDbCustom,
} from "./utils/recipeHelpers";
import {
  replaceRecipeInPlan,
  removeRecipeFromPlan
} from "./utils/planRecipeHelpers";
import { replaceProgrammeMeal } from "./utils/programmeMealHelpers";
import {
  renderSettingsIcon,
  renderSettingsRow,
  renderSettingsInfoRow,
  renderSettingsToggleRow
} from "./utils/settingsUI";

import { useRecipeForm } from "./hooks/useRecipeForm";
import { useLocalStorageState } from "./hooks/useLocalStorageState";
import { useSettingsState } from "./hooks/useSettingsState";

import FeedbackPage from "./components/FeedbackPage";
import RecipeDetailsModal from "./components/RecipeDetailsModal";
import HomeDayModal from "./components/HomeDayModal";
import AddWeeksModal from "./components/AddWeeksModal";
import ProgrammePickerModal from "./components/ProgrammePickerModal";
import SplashScreen from "./components/SplashScreen";
import OfflineScreen from "./components/OfflineScreen";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ProgrammeDetailsModal from "./components/ProgrammeDetailsModal";
import MealSlotSheet from "./components/MealSlotSheet";
import MealChoiceSheet from "./components/MealChoiceSheet";
import MealTimePickerModal from "./components/MealTimePickerModal";
import PlanToast from "./components/PlanToast";
import EditRecipeModal from "./components/EditRecipeModal";
import RecipeGridCard from "./components/RecipeGridCard";
import BottomNav from "./components/BottomNav";
import SettingsProfileCard from "./components/SettingsProfileCard";
import ManualEntryModal from "./components/ManualEntryModal";
import ProgrammeSuccessModal from "./components/ProgrammeSuccessModal";
import CookingMode from "./components/CookingMode";

import {primaryButtonStyle, secondaryButtonStyle, closeButtonStyle } from "./styles/buttonStyles";
import { modalOverlayStyle, modalContentStyle } from "./styles/modalStyles";
import { modalAnimationStyle } from "./styles/animationStyles";
import { globalHelperStyles } from "./styles/globalStyles";
import {smallSecondaryButtonStyle} from "./styles/appStyles";

import { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from "react";

// calculateNutritionTargets is imported from src/utils/nutritionCalc.js

const defaultKitchenStaples = [
  "olive oil", "salt", "black pepper", "garlic", "butter",
  "vegetable oil", "paprika", "cumin", "oregano", "basil",
  "chilli flakes", "onion powder", "garlic powder", "soy sauce",
  "honey", "flour", "sugar", "baking powder", "vinegar",
  "tomato puree", "chicken stock", "vegetable stock", "bay leaves",
  "thyme", "rosemary", "cinnamon", "turmeric", "ginger"
];

function App() {
  // ── Auth session (Supabase) ───────────────────────────────────────────────
  const [session, setSession] = useState(undefined); // undefined = loading, null = signed out

  const [showSplash, setShowSplash] = useState(() => {
    if (sessionStorage.getItem("splashShown")) return false;
    sessionStorage.setItem("splashShown", "1");
    return true;
  });
  const handleSplashComplete = useCallback(() => setShowSplash(false), []);

  // Onboarding routing state
  // authMode: which screen to show when signed out ("onboarding" = Welcome flow, "signin" = existing AuthScreen)
  // hasCompletedOnboarding: true by default to avoid flash for existing users while profile loads
  const [authMode, setAuthMode] = useState("onboarding");
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true);
  const [pendingQuizData, setPendingQuizData] = useState(null); // { data, targets } set when user navigates from onboarding quiz to sign-in
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  useEffect(() => {
    // Belt-and-braces: if the URL hash contains type=recovery the user arrived via a
    // password-reset email link. Set the flag immediately — before getSession() resolves —
    // so we never miss the PASSWORD_RECOVERY event due to listener-registration timing.
    if (window.location.hash.includes("type=recovery")) {
      setShowPasswordReset(true);
    }

    // Restore persisted session on mount
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
    });
    // Listen for future sign-in / sign-out events
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession ?? null);
      if (event === "PASSWORD_RECOVERY") {
        setShowPasswordReset(true);
        return;
      }
      if (newSession?.user?.id) {
        posthog.identify(newSession.user.id); // no PII — ID only
      } else {
        posthog.reset();
        // Reset React state so the plan sync useEffect doesn't fire with stale data
        // before the new user's data loads, and so the app always opens on Home.
        setPlan({});
        planInitialisedRef.current = false;
        setActiveTab("home");
        setHasCreatedManualPlan(false);
        setHasChosenProgramme(false);
        // Clear user-specific data on every sign-out (explicit or session expiry) so
        // that a different user signing in on the same device starts clean.
        // Migration flags must be removed so their migrations re-run for the new account.
        localStorage.removeItem("ls_migrated_v1");
        localStorage.removeItem("ls_migrated_v2");
        localStorage.removeItem("ls_migrated_v3");
        // Plan data — the v3 migration reads localStorage["plan"] to seed meal_plan_entries.
        // Without removing this, a previous user's plan is silently uploaded to a new account.
        console.log("[auth] clearing plan from localStorage:", localStorage.getItem("plan"));
        localStorage.removeItem("plan");
        console.log("[auth] plan after removal:", localStorage.getItem("plan"));
        localStorage.removeItem("manualWeeks");       // dead state, but may still exist
        localStorage.removeItem("selectedProgramme");
        localStorage.removeItem("programmeWeekIndex");
        setAuthMode("onboarding");
        setHasCompletedOnboarding(true); // reset so next user doesn't see stale false
        setPendingQuizData(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // MIGRATED: recipes now fetched from Supabase (public-recipes useEffect below)
  const [recipes, setRecipes] = useState([]);
  const [recipesLoading, setRecipesLoading] = useState(true);

  // ── Supabase data sync on login ────────────────────────────────────────────
  // Public recipes are fetched here (not on mount) to avoid a race condition:
  // a separate useEffect([]) for public recipes plus a login useEffect for custom
  // recipes means both call setRecipes, and whichever resolves last wins —
  // the full-replace from the public fetch can silently discard custom recipes.
  // Fetching all three (public, custom, favourite IDs) in one Promise.all sets
  // state once and eliminates the race entirely.
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) return;

    // Seed profileEmail from the auth session immediately.
    // user_profiles fetch below will override with the stored display email if present.
    // Without this, existing users whose user_profiles.email is null see a blank field.
    if (session?.user?.email) setProfileEmail(session.user.email);

    // SUPABASE: load saved meals
    setSavedMealsLoading(true);
    supabase.from("saved_meals").select("*").eq("user_id", userId).order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) setSavedManualMeals(data);
        setSavedMealsLoading(false);
      });

    // SUPABASE: load user profile (name, email, nutrition goals, photo, dataHub, dietary pref)
    supabase.from("user_profiles").select("*").eq("id", userId).single()
      .then(({ data, error }) => {
        if (error || !data) return;
        if (data.name)  setProfileName(data.name);
        if (data.email) setProfileEmail(data.email);
        if (data.profile_image != null)      setProfileImage(data.profile_image || "");
        if (data.dietary_preference != null) setDietaryPreference(data.dietary_preference || "none");
        if (data.gender || data.dob || data.current_weight) {
          setDataHub(prev => ({
            ...prev,
            gender:       data.gender         ?? prev.gender,
            dob:          data.dob             ?? prev.dob,
            currentWeight: data.current_weight ?? prev.currentWeight,
            goalWeight:    data.goal_weight    ?? prev.goalWeight,
            height:        data.height         ?? prev.height,
            activityLevel: data.activity_level ?? prev.activityLevel,
            goal:          data.goal           ?? prev.goal,
          }));
        }
        if (data.daily_calorie_goal != null) {
          setNutritionTargets(prev => ({
            ...prev,
            calories: data.daily_calorie_goal ?? prev.calories,
            protein:  data.daily_protein_goal  ?? prev.protein,
            carbs:    data.daily_carbs_goal     ?? prev.carbs,
            fats:     data.daily_fats_goal      ?? prev.fats,
          }));
        }
        if (data.has_created_manual_plan) setHasCreatedManualPlan(true);
        if (data.has_chosen_programme)    setHasChosenProgramme(true);
        if (data.has_completed_onboarding === false) setHasCompletedOnboarding(false);
      });

    // SUPABASE: load daily notes → rebuild { [date]: note } map
    supabase.from("daily_notes").select("date, note").eq("user_id", userId)
      .then(({ data, error }) => {
        if (!error && data) {
          const notes = {};
          data.forEach(row => { notes[row.date] = row.note; });
          setDailyNotes(notes);
        }
      });

    // SUPABASE: load recipe notes → rebuild { [recipeName]: note } map
    supabase.from("recipe_notes").select("recipe_name, note").eq("user_id", userId)
      .then(({ data, error }) => {
        if (!error && data) {
          const notes = {};
          data.forEach(row => { notes[row.recipe_name] = row.note; });
          setRecipeNotes(notes);
        }
      });

    // SUPABASE: load completed programmes (for Trophy Room)
    supabase.from("completed_programmes").select("*").eq("user_id", userId)
      .order("completed_at", { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) {
          setCompletedProgrammes(data.map(row => ({
            id:          row.programme_id,
            name:        row.programme_name,
            completedAt: row.completed_at,
          })));
        }
      });

    // SUPABASE: fetch public recipes, custom recipes, favourite IDs, and plan together.
    // Plan is loaded here so recipe objects can be reconstructed immediately from the
    // freshly-merged recipe list — avoids a separate round trip after recipes load.
    Promise.all([
      supabase.from("recipes").select("*").eq("is_published", true),
      supabase.from("custom_recipes").select("*").eq("user_id", userId),
      supabase.from("favourite_recipes").select("recipe_id, custom_recipe_id").eq("user_id", userId).order("created_at", { ascending: false }),
      supabase.from("meal_plan_entries").select("*").eq("user_id", userId),
    ]).then(([pubRes, custRes, favRes, planRes]) => {
      const pub  = !pubRes.error  && pubRes.data  ? pubRes.data.map(r => dbRecipeToApp(r, false)) : [];
      const cust = !custRes.error && custRes.data ? custRes.data.map(r => dbRecipeToApp(r, true))  : [];
      const all  = [...pub, ...cust];
      setRecipes(all);
      setRecipesLoading(false);

      if (!favRes.error && favRes.data) {
        // Build ordered list in created_at DESC order from the query (most recently saved first)
        const orderedFavNames = favRes.data
          .map(row => {
            const id = row.recipe_id || row.custom_recipe_id;
            return all.find(r => r.id === id)?.name;
          })
          .filter(Boolean);
        setFavouriteRecipes(orderedFavNames);
      }

      if (!planRes.error && planRes.data) {
        if (planRes.data.length > 0) {
          // Supabase has plan data — reconstruct the nested plan object.
          // Recipes are matched by UUID first, then name, then a name-only stub as fallback.
          const rebuilt = {};
          planRes.data.forEach(row => {
            if (!rebuilt[row.date]) {
              rebuilt[row.date] = { breakfast: null, lunch: null, dinner: null, snacks: [] };
            }
            if (row.meal_type === "snacks") {
              rebuilt[row.date].snacks = Array.isArray(row.snack_data) ? row.snack_data : [];
            } else {
              let recipe = null;
              if (row.recipe_id)        recipe = all.find(r => r.id === row.recipe_id);
              if (!recipe && row.custom_recipe_id) recipe = all.find(r => r.id === row.custom_recipe_id);
              if (!recipe && row.meal_name)        recipe = all.find(r => r.name === row.meal_name);
              if (!recipe && row.meal_name)        recipe = { name: row.meal_name }; // stub
              rebuilt[row.date][row.meal_type] = recipe;
            }
          });
          setPlan(rebuilt);
        } else if (!localStorage.getItem("ls_migrated_v3")) {
          // First launch after Phase 5 deploy: Supabase is empty but localStorage
          // still has the plan. Show it immediately — the v3 migration (which runs
          // shortly after in the migration useEffect) will persist it to Supabase.
          const rawPlan = localStorage.getItem("plan");
          if (rawPlan) {
            try { setPlan(JSON.parse(rawPlan)); } catch { /* ignore */ }
          }
        }
      }

      // Mark plan as initialised so the sync useEffect can start writing safely.
      planInitialisedRef.current = true;
      console.log("[plan] planInitialisedRef set to true — plan entries from Supabase:", planRes.data?.length ?? 0);
    });

    // SUPABASE: load completed meals for last 90 days → rebuild local map
    const since = new Date();
    since.setDate(since.getDate() - 90);
    const sinceStr = since.toISOString().split("T")[0];
    supabase.from("completed_meals").select("date, meal_type")
      .eq("user_id", userId).gte("date", sinceStr)
      .then(({ data, error }) => {
        if (!error && data) {
          const built = {};
          data.forEach(row => { built[`${row.date}-${row.meal_type}`] = true; });
          setCompletedMeals(built);
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  // ── One-time localStorage → Supabase migration ────────────────────────────
  // Runs once per account, after recipes are loaded so name→UUID lookup works.
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId || recipesLoading) return;
    // Note: each migration function checks its own flag — no outer guard here,
    // because a guard on ls_migrated_v1 would silently block v2 and v3 too.

    async function migrate() {
      if (localStorage.getItem("ls_migrated_v1")) return;
      // 1. Custom recipes
      const recipesRaw = localStorage.getItem("recipes");
      if (recipesRaw) {
        try {
          const saved = JSON.parse(recipesRaw);
          const custom = saved.filter(r => r.custom === true);
          if (custom.length > 0) {
            const { data, error } = await supabase
              .from("custom_recipes")
              .insert(custom.map(r => appRecipeToDbCustom(r, userId)))
              .select();
            if (!error && data) {
              const migrated = data.map(row => dbRecipeToApp(row, true));
              setRecipes(prev => [...prev.filter(r => !r.custom), ...migrated]);
            }
          }
        } catch { /* ignore parse errors */ }
      }

      // 2. Completed meals (date-keyed only)
      const completedRaw = localStorage.getItem("completedMeals");
      if (completedRaw) {
        try {
          const map = JSON.parse(completedRaw);
          const ISO_KEY_RE = /^\d{4}-\d{2}-\d{2}-(breakfast|lunch|dinner|snacks)$/;
          const rows = Object.entries(map)
            .filter(([key, val]) => val === true && ISO_KEY_RE.test(key))
            .map(([key]) => {
              const parts = key.split("-");
              return {
                user_id: userId,
                date: parts.slice(0, 3).join("-"),
                meal_type: parts[3],
              };
            });
          if (rows.length > 0) {
            await supabase.from("completed_meals").insert(rows);
          }
        } catch { /* ignore */ }
      }

      // 3. Favourite recipes (name → UUID lookup in current recipes state)
      const favRaw = localStorage.getItem("favouriteRecipes");
      if (favRaw) {
        try {
          const favNames = JSON.parse(favRaw);
          if (favNames.length > 0) {
            const rows = favNames.reduce((acc, name) => {
              // recipes state is available via closure — populated before this effect runs
              const recipe = recipes.find(r => r.name === name);
              if (!recipe?.id) return acc;
              const col = recipe.custom ? "custom_recipe_id" : "recipe_id";
              return [...acc, { user_id: userId, [col]: recipe.id }];
            }, []);
            if (rows.length > 0) {
              await supabase.from("favourite_recipes").insert(rows);
              setFavouriteRecipes(favNames);
            }
          }
        } catch { /* ignore */ }
      }

      localStorage.setItem("ls_migrated_v1", "1");
    }

    migrate();

    // ── v2 migration: profile fields, daily/recipe notes, completed programmes ──
    async function migrateV2() {
      if (localStorage.getItem("ls_migrated_v2")) return;

      const profileUpsert = {};

      const rawImage = localStorage.getItem("profileImage");
      if (rawImage) profileUpsert.profile_image = rawImage;

      const rawDietPref = localStorage.getItem("dietaryPreference");
      if (rawDietPref) profileUpsert.dietary_preference = rawDietPref;

      const rawDataHub = localStorage.getItem("dataHub");
      if (rawDataHub) {
        try {
          const dh = JSON.parse(rawDataHub);
          if (dh.gender)       profileUpsert.gender          = dh.gender;
          if (dh.dob)          profileUpsert.dob              = dh.dob;
          if (dh.currentWeight) profileUpsert.current_weight  = dh.currentWeight;
          if (dh.goalWeight)   profileUpsert.goal_weight      = dh.goalWeight;
          if (dh.height)       profileUpsert.height           = dh.height;
          if (dh.activityLevel) profileUpsert.activity_level  = dh.activityLevel;
          if (dh.goal)         profileUpsert.goal             = dh.goal;
        } catch { /* ignore */ }
      }

      if (Object.keys(profileUpsert).length > 0) {
        await supabase.from("user_profiles")
          .upsert({ id: userId, ...profileUpsert }, { onConflict: "id" });
      }

      // daily notes
      const rawDailyNotes = localStorage.getItem("dailyNotes");
      if (rawDailyNotes) {
        try {
          const notesMap = JSON.parse(rawDailyNotes);
          const rows = Object.entries(notesMap)
            .filter(([, note]) => note)
            .map(([date, note]) => ({ user_id: userId, date, note }));
          if (rows.length > 0) await supabase.from("daily_notes").insert(rows);
        } catch { /* ignore */ }
      }

      // recipe notes
      const rawRecipeNotes = localStorage.getItem("recipeNotes");
      if (rawRecipeNotes) {
        try {
          const notesMap = JSON.parse(rawRecipeNotes);
          const rows = Object.entries(notesMap)
            .filter(([, note]) => note)
            .map(([recipe_name, note]) => ({ user_id: userId, recipe_name, note }));
          if (rows.length > 0) await supabase.from("recipe_notes").insert(rows);
        } catch { /* ignore */ }
      }

      // completed programmes
      const rawCompleted = localStorage.getItem("completedProgrammes");
      if (rawCompleted) {
        try {
          const progs = JSON.parse(rawCompleted);
          const rows = progs
            .filter(p => p.name)
            .map(p => ({
              user_id:        userId,
              programme_id:   String(p.id || p.name),
              programme_name: p.name,
              completed_at:   p.completedAt || new Date().toISOString(),
            }));
          if (rows.length > 0) await supabase.from("completed_programmes").insert(rows);
        } catch { /* ignore */ }
      }

      localStorage.setItem("ls_migrated_v2", "1");
    }

    migrateV2();

    // ── v3 migration: plan localStorage → meal_plan_entries ──────────────────
    async function migrateV3() {
      if (localStorage.getItem("ls_migrated_v3")) return;
      const planInStorage = localStorage.getItem("plan");
      console.log("[v3 migration] running — localStorage[\"plan\"] is:", planInStorage ? `${planInStorage.length} chars (has data)` : "null/empty");

      // Skip if the table already has rows for this user (plan sync already ran,
      // or a previous migration attempt partially succeeded).
      const { count } = await supabase
        .from("meal_plan_entries")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId);
      if (count && count > 0) {
        localStorage.setItem("ls_migrated_v3", "1");
        return;
      }

      const rawPlan = localStorage.getItem("plan");
      if (!rawPlan) { localStorage.setItem("ls_migrated_v3", "1"); return; }

      try {
        const savedPlan = JSON.parse(rawPlan);
        const rows = [];
        const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

        Object.entries(savedPlan).forEach(([date, dayPlan]) => {
          if (!dayPlan || !ISO_DATE_RE.test(date)) return; // skip old day-name keys

          ["breakfast", "lunch", "dinner"].forEach(mealType => {
            const recipe = dayPlan[mealType];
            if (!recipe) return;
            rows.push({
              user_id:          userId,
              date,
              meal_type:        mealType,
              recipe_id:        (!recipe.custom && recipe.id) ? recipe.id : null,
              custom_recipe_id: (recipe.custom  && recipe.id) ? recipe.id : null,
              meal_name:        recipe.name || null,
            });
          });

          const snacks = dayPlan.snacks || [];
          if (snacks.length > 0) {
            rows.push({ user_id: userId, date, meal_type: "snacks", snack_data: snacks });
          }
        });

        if (rows.length > 0) {
          const { error } = await supabase.from("meal_plan_entries").insert(rows);
          if (error) { console.error("plan migration insert:", error); return; }
        }
      } catch { /* ignore parse errors */ }

      localStorage.setItem("ls_migrated_v3", "1");
    }

    migrateV3();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, recipesLoading]);


const {
  editingRecipeName,

  newRecipeName,
  setNewRecipeName,
  newRecipeImage,
  setNewRecipeImage,
  newRecipeDescription,
  setNewRecipeDescription,
  newRecipeTime,
  setNewRecipeTime,
  newRecipeCalories,
  setNewRecipeCalories,
  newRecipeServings,
  setNewRecipeServings,
  newRecipeIngredients,
  setNewRecipeIngredients,
  newRecipeCategory,
  setNewRecipeCategory,
  newRecipeTag,
  setNewRecipeTag,
  newRecipeProtein,
  setNewRecipeProtein,
  newRecipeCarbs,
  setNewRecipeCarbs,
  newRecipeFats,
  setNewRecipeFats,
  newRecipeInstructions,
  setNewRecipeInstructions,
  activeNewRecipeSections,
  setActiveNewRecipeSections,

  editRecipeName,
  setEditRecipeName,
  editRecipeImage,
  setEditRecipeImage,
  editRecipeDescription,
  setEditRecipeDescription,
  editRecipeTime,
  setEditRecipeTime,
  editRecipeCalories,
  setEditRecipeCalories,
  editRecipeServings,
  setEditRecipeServings,
  editRecipeIngredients,
  setEditRecipeIngredients,
  editRecipeCategory,
  setEditRecipeCategory,
  editRecipeTag,
  setEditRecipeTag,
  editRecipeProtein,
  setEditRecipeProtein,
  editRecipeCarbs,
  setEditRecipeCarbs,
  editRecipeFats,
  setEditRecipeFats,
  editRecipeInstructions,
  setEditRecipeInstructions,
  activeEditRecipeSections,
  setActiveEditRecipeSections,

  addRecipeWarning,
  setAddRecipeWarning,
  hasTriedToSaveAddRecipe,
  setHasTriedToSaveAddRecipe,

  editRecipeWarning,
  setEditRecipeWarning,
  hasTriedToSaveEditRecipe,
  setHasTriedToSaveEditRecipe,

  clearAddRecipeForm,
  cancelEditingRecipe,
  startEditingRecipe,

  isAddRecipeValid,
  isNewRecipeNameFilled,
  isNewRecipeCategoryFilled,
  isNewRecipeIngredientsFilled,
  isNewRecipeCaloriesFilled,
  isNewRecipeProteinFilled,
  isNewRecipeCarbsFilled,
  isNewRecipeFatsFilled,

  isEditRecipeValid,
  isEditRecipeNameFilled,
  isEditRecipeCategoryFilled,
  isEditRecipeIngredientsFilled,
  isEditRecipeCaloriesFilled,
  isEditRecipeProteinFilled,
  isEditRecipeCarbsFilled,
  isEditRecipeFatsFilled
} = useRecipeForm();

const {
  showProfilePhotoActions,
  setShowProfilePhotoActions,
  showSettingsModal,
  setShowSettingsModal,
  isClosingSettingsModal,
  settingsPage,
  setSettingsPage,
  settingsPageDirection,
  setSettingsPageDirection,

  isEditingAccount,
  setIsEditingAccount,
  editName,
  setEditName,
  editEmail,
  setEditEmail,
  editNameWarning,
  setEditNameWarning,

  showDeleteConfirm,
  setShowDeleteConfirm,
  deleteConfirmText,
  setDeleteConfirmText,
  showClearDataConfirm,
  setShowClearDataConfirm,

  supportMessages,
  setSupportMessages,
  supportInput,
  setSupportInput,
  supportIsTyping,
  setSupportIsTyping,
  supportError,
  setSupportError,
  expandedFaqs,
  setExpandedFaqs,

  closeSettingsModal,
  openSettingsPage,
  goBackSettingsPage,
} = useSettingsState();

function generateWeek() {
  const mealTypes = ["breakfast", "lunch", "dinner"];
  const usedRecipes = new Set();

  const currentPlanRecipes = Object.values(plan).flatMap((dayPlan) =>
    Object.values(dayPlan || {}).filter(Boolean).map((meal) => meal.name)
  );
  currentPlanRecipes.forEach((name) => usedRecipes.add(name));

  const getRecipeForSlot = (mealType) => {
    const filtered = recipes.filter((recipe) => {
      const matchesCategory = recipe.categories.includes(mealType);
      const notUsed = !usedRecipes.has(recipe.name);
      const matchesPreference =
        dietaryPreference === "none" ||
        recipe.tags.includes(dietaryPreference) ||
        recipe.categories.includes(dietaryPreference);
      return matchesCategory && notUsed && matchesPreference;
    });

    if (filtered.length === 0) {
      const fallback = recipes.filter((recipe) =>
        recipe.categories.includes(mealType)
      );
      if (fallback.length === 0) return null;
      return fallback[Math.floor(Math.random() * fallback.length)];
    }

    const pick = filtered[Math.floor(Math.random() * filtered.length)];
    usedRecipes.add(pick.name);
    return pick;
  };

  const weekDays = getPlanCalendarDays(planCalendarWeekOffset);

  setPlan((prevPlan) => {
    const updatedPlan = { ...prevPlan };

    weekDays.forEach((date) => {
      const dateKey = getDateKey(date);
      updatedPlan[dateKey] = updatedPlan[dateKey] || {};

      mealTypes.forEach((mealType) => {
        if (!updatedPlan[dateKey][mealType]) {
          const recipe = getRecipeForSlot(mealType);
          if (recipe) {
            updatedPlan[dateKey][mealType] = recipe;
          }
        }
      });
    });

    return updatedPlan;
  });

  setPlanToastMessage("Week generated based on your preferences!");
}

  // MIGRATED: plan now loaded from Supabase meal_plan_entries (see login useEffect)
  const [plan, setPlan] = useState({});
  // MIGRATED: plan intro flags now backed by Supabase user_profiles
  const [hasCreatedManualPlan, setHasCreatedManualPlan] = useState(false);
  const [hasChosenProgramme,   setHasChosenProgramme]   = useState(false);

const [planningMode, setPlanningMode] = useLocalStorageState("planningMode", "manual");
const [showManualEntryModal, setShowManualEntryModal] = useState(false);
const [manualEntryDateKey, setManualEntryDateKey] = useState(null);
const [manualEntryMode, setManualEntryMode] = useState("log");
const [manualEntryMealType, setManualEntryMealType] = useState(null);
const [manualEntryInitialValues, setManualEntryInitialValues] = useState(null);
const [manualEntryEditingSnackId, setManualEntryEditingSnackId] = useState(null);
const [isFullPageRecipe, setIsFullPageRecipe] = useState(false);
const [recipeFullPageContext, setRecipeFullPageContext] = useState(null);
const [cookingModeRecipe, setCookingModeRecipe] = useState(null);
const [cookingModeContext, setCookingModeContext] = useState(null);

  const [selectedProgramme, setSelectedProgramme] = useState(null);
  // MIGRATED: completedProgrammes now backed by Supabase completed_programmes table
  // eslint-disable-next-line no-unused-vars
  const [completedProgrammes, setCompletedProgrammes] = useState([]);
  const [showProgrammeSuccessModal, setShowProgrammeSuccessModal] = useState(false);
  const [currentProgrammeWeekIndex, setCurrentProgrammeWeekIndex] = useState(0);
  const [weekTransitionDirection, setWeekTransitionDirection] = useState("forward");
  // programmeHistory removed — was written-only (never displayed) and kept only in localStorage

  const [manualItems, setManualItems] = useLocalStorageState("manualItems", []);
// SUPABASE: saved_meals table (replaced localStorage "savedManualMeals")
const [savedManualMeals, setSavedManualMeals] = useState([]);
const [savedMealsLoading, setSavedMealsLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useLocalStorageState("checkedItems", {});
  const [hiddenStaples, setHiddenStaples] = useLocalStorageState("hiddenStaples", []);
  const [pantryItems, setPantryItems] = useLocalStorageState("pantryItems", []);
  const [servingSizePreference] = useLocalStorageState("servingSizePreference", 1);
  const [removedDefaultStaples, setRemovedDefaultStaples] = useLocalStorageState("removedDefaultStaples", []);
  const [customStaples, setCustomStaples] = useLocalStorageState("customStaples", []);

  // MIGRATED: favourites now backed by Supabase favourite_recipes table
  const [favouriteRecipes, setFavouriteRecipes] = useState([]);

  const [expandedMacroDays, setExpandedMacroDays] = useState([]);

function toggleMacroDay(day) {
  setExpandedMacroDays((prev) =>
    prev.includes(day)
      ? prev.filter((d) => d !== day)
      : [...prev, day]
  );
}

  const [newItem, setNewItem] = useState("");
  const [activeTab, setActiveTab] = useState("home");
  const [recipeSearch, setRecipeSearch] = useState("");

const [showMealSlotSheet, setShowMealSlotSheet] = useState(false);
const [showMealChoiceSheet, setShowMealChoiceSheet] = useState(false);
const [mealChoiceTarget, setMealChoiceTarget] = useState(null); // { dateKey, mealType }
const [mealSlotSheetTarget, setMealSlotSheetTarget] = useState(null);
const [mealSlotSearch, setMealSlotSearch] = useState("");
const [isClosingMealSlotSheet, setIsClosingMealSlotSheet] = useState(false);
const [mealSlotSelectedRecipe, setMealSlotSelectedRecipe] = useState(null);


const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);
const [recipesShowFavouritesOnly, setRecipesShowFavouritesOnly] = useState(false);
const [recipesShowMyRecipesOnly, setRecipesShowMyRecipesOnly] = useState(false);

const [slotSelectQueue, setSlotSelectQueue] = useState([]);
const [showFullWeek, setShowFullWeek] = useState(false);

const [mealSheetExpanded, setMealSheetExpanded] = useState(false);
const [mealSheetDragY, setMealSheetDragY] = useState(0);
const mealSheetDragStartY = useRef(null);
const mealSheetIsDragging = useRef(false);
const tabContentRef = useRef(null);
// Guards the plan sync useEffect — only fires after the initial Supabase load,
// preventing a startup overwrite of freshly-loaded plan data.
const planInitialisedRef = useRef(false);

const [selectedPlanDay, setSelectedPlanDay] = useState(() => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
});

const [planCalendarWeekOffset, setPlanCalendarWeekOffset] = useState(0);

  const [showRemoveAllWeeksConfirm, setShowRemoveAllWeeksConfirm] = useState(false);

  const [showAddRecipeForm, setShowAddRecipeForm] = useState(false);
  
  const [hoveredButton, setHoveredButton] = useState(null);
  const [showClearPlanConfirm, setShowClearPlanConfirm] = useState(false);

  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showDeleteRecipeConfirm, setShowDeleteRecipeConfirm] = useState(false);
  const [recipePendingDelete, setRecipePendingDelete] = useState(null);
  const [selectedHomeDay, setSelectedHomeDay] = useState(null);
  const [homeMealIndex, setHomeMealIndex] = useState(0);

  const [activeFilters, setActiveFilters] = useState([]);

  const [dragOffset, setDragOffset] = useState(0);

  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [sortBy, setSortBy] = useState("default");
  const [recipeModalView, setRecipeModalView] = useState("details");
  const [recipeModalDirection, setRecipeModalDirection] = useState("forward");

  const [selectedPlanSlots, setSelectedPlanSlots] = useState([]);
  const [planToastMessage, setPlanToastMessage] = useState("");
  const [showAddWeeksModal, setShowAddWeeksModal] = useState(false);
  const [profileCalendarDate, setProfileCalendarDate] = useState(
  () => new Date()
);

// MIGRATED: completed meals now backed by Supabase (date-keyed only; programme keys stay local)
const [completedMeals, setCompletedMeals] = useState({});
// completionHistory removed — was never written to; streak now derived from completedMeals
// MIGRATED: dailyNotes now backed by Supabase daily_notes table
const [dailyNotes, setDailyNotes] = useState({});
// MIGRATED: recipeNotes now backed by Supabase recipe_notes table
const [recipeNotes, setRecipeNotes] = useState({});
// lastActiveAt removed — the inactiveTooLong variable it computed was marked no-unused-vars

// MIGRATED: profile fields now backed by Supabase user_profiles table
const [profileImage, setProfileImage] = useState("");
const [profileName, setProfileName] = useState("");
const [dataHub, setDataHub] = useState({
  gender: "", dob: "", currentWeight: "", goalWeight: "",
  height: "", activityLevel: "", goal: ""
});
const [nutritionTargets, setNutritionTargets] = useState({ calories: 0, protein: 0, carbs: 0, fats: 0 });
const [profileEmail, setProfileEmail] = useState("");

const [defaultServings, setDefaultServings] = useLocalStorageState("defaultServings", "2", {
  parse: (value) => value,
  stringify: (value) => value
});

// MIGRATED: dietaryPreference now backed by Supabase user_profiles.dietary_preference
const [dietaryPreference, setDietaryPreference] = useState("none");

const [measurementUnit, setMeasurementUnit] = useLocalStorageState("measurementUnit", "grams", {
  parse: (value) => value,
  stringify: (value) => value
});

const [breakfastTime, setBreakfastTime] = useLocalStorageState("breakfastTime", "8:00 AM", {
  parse: (value) => value,
  stringify: (value) => value
});

const [lunchTime, setLunchTime] = useLocalStorageState("lunchTime", "12:30 PM", {
  parse: (value) => value,
  stringify: (value) => value
});

const [dinnerTime, setDinnerTime] = useLocalStorageState("dinnerTime", "6:30 PM", {
  parse: (value) => value,
  stringify: (value) => value
});

const [mealReminders, setMealReminders] = useLocalStorageState("mealReminders", "Off", {
  parse: (value) => value,
  stringify: (value) => value
});

const [planReminders, setPlanReminders] = useLocalStorageState("planReminders", "On", {
  parse: (value) => value,
  stringify: (value) => value
});

const [programmeReminders, setProgrammeReminders] = useLocalStorageState("programmeReminders", "On", {
  parse: (value) => value,
  stringify: (value) => value
});

const [appUpdates, setAppUpdates] = useLocalStorageState("appUpdates", "On", {
  parse: (value) => value,
  stringify: (value) => value
});

  const [showProgrammePicker, setShowProgrammePicker] = useState(false);
  const [selectedProgrammeDetail, setSelectedProgrammeDetail] = useState(null);
  const [programmeSearch, setProgrammeSearch] = useState("");
  const [planSelectedSlots, setPlanSelectedSlots] = useState([]); 
  const [programmeMealEditTarget, setProgrammeMealEditTarget] = useState(null); 

const [activeMealTimePicker, setActiveMealTimePicker] = useState(null);
const [tempHour, setTempHour] = useState("8");
const [tempMinute, setTempMinute] = useState("00");
const [tempPeriod, setTempPeriod] = useState("AM");

  const homeDaysCarouselRef = useRef(null);
  const profilePhotoActionsRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const homeCarouselFrameRef = useRef(null);

  const isDraggingRef = useRef(false);
const startXRef = useRef(0);
const currentXRef = useRef(0);
const deltaXRef = useRef(0);

const dragRefs = {
  isDraggingRef,
  startXRef,
  currentXRef,
  deltaXRef
};

const settingsSwipeStartXRef = useRef(null);

  const updateHomeCarouselVisuals = useCallback(() => {
  if (!homeDaysCarouselRef.current) return;

  const container = homeDaysCarouselRef.current;
  const cards = container.querySelectorAll("[data-day]");
  const containerCenter = container.scrollLeft + container.clientWidth / 2;

  cards.forEach((card) => {
    const cardCenter = card.offsetLeft + card.clientWidth / 2;
    const distance = Math.abs(containerCenter - cardCenter);

    const maxDistance = container.clientWidth / 2;
    const clamped = Math.min(distance / maxDistance, 1);

    const scale = 1.015 - clamped * 0.07;
    const opacity = 1 - clamped * 0.18;
    const saturation = 1 - clamped * 0.14;

    card.style.transform = `scale(${scale})`;
    card.style.opacity = opacity;
    card.style.filter = `saturate(${saturation})`;
  });
}, []);

function handleSaveSnack(snackData) {
  if (manualEntryMode === "ate-different") {
    handleAteElse(snackData);
    return;
  }
  const dateKey = manualEntryDateKey || getDateKey(new Date());
  const editId = manualEntryEditingSnackId;
  setPlan((prev) => {
    const dayPlan = prev[dateKey] || {};
    const existingSnacks = dayPlan.snacks || [];
    const updatedSnacks = editId
      ? existingSnacks.map((s) => s.id === editId ? { ...snackData, id: editId } : s)
      : [...existingSnacks, { ...snackData, id: Date.now() }];
    return { ...prev, [dateKey]: { ...dayPlan, snacks: updatedSnacks } };
  });
  setManualEntryEditingSnackId(null);
  setManualEntryInitialValues(null);
}

function openEditSnackModal(snack, dateKey) {
  setManualEntryInitialValues(snack);
  setManualEntryEditingSnackId(snack.id);
  setManualEntryDateKey(dateKey || getDateKey(new Date()));
  setManualEntryMode("log");
  setShowManualEntryModal(true);
}

function openManualEntryModal(dateKey, mode = "log", mealType = null) {
  setManualEntryDateKey(dateKey || getDateKey(new Date()));
  setManualEntryMode(mode);
  setManualEntryMealType(mealType);
  setShowManualEntryModal(true);
}

function handleAteElse(data) {
  const dateKey = manualEntryDateKey || getDateKey(new Date());
  const mealType = manualEntryMealType;
  if (!mealType) return;
  setPlan(prev => {
    const dayPlan = prev[dateKey] || {};
    return {
      ...prev,
      [dateKey]: {
        ...dayPlan,
        [mealType]: {
          name: data.name,
          calories: data.calories,
          macros: { protein: data.protein, carbs: data.carbs, fats: data.fats },
        }
      }
    };
  });
}

function removeSnack(dateKey, snackId) {
  setPlan((prev) => {
    const dayPlan = prev[dateKey] || {};
    return {
      ...prev,
      [dateKey]: {
        ...dayPlan,
        snacks: (dayPlan.snacks || []).filter((s) => s.id !== snackId)
      }
    };
  });
}

function handleSettingsSwipeTouchStart(e) {
  settingsSwipeStartXRef.current = e.touches[0].clientX;
}

function handleSettingsSwipeTouchMove(e) {
  // optional: could add visual resistance here later
}

function handleSettingsSwipeTouchEnd(e) {
  if (settingsSwipeStartXRef.current === null) return;
  const deltaX = e.changedTouches[0].clientX - settingsSwipeStartXRef.current;
  settingsSwipeStartXRef.current = null;

  if (deltaX > 100 && settingsPage !== "home") {
    goBackSettingsPage();
  }
}

function getMealCardBackground(meal, isFront = false) {
  if (meal?.image) {
    return `url(${meal.image})`;
  }
  if (isFront) {
    return "linear-gradient(145deg, #1e293b 0%, #0f172a 60%, #020617 100%)";
  }
  return "linear-gradient(145deg, #1e293b 0%, #0f172a 100%)";
}

function openMealTimePicker(meal) {
  const pickerState = getMealTimePickerState(
    meal,
    breakfastTime,
    lunchTime,
    dinnerTime
  );

  setTempHour(pickerState.hour);
  setTempMinute(pickerState.minute);
  setTempPeriod(pickerState.period);
  setActiveMealTimePicker(pickerState.meal);
}

function openMealSlotSheet(dateKey, mealType) {
  setMealSlotSheetTarget({ dateKey, mealType });
  setMealSlotSearch("");
  setMealSlotSelectedRecipe(null);
  setMealSheetExpanded(false);
  setShowMealSlotSheet(true);
}

function openMealChoice(dateKey, mealType) {
  posthog.capture("meal_slot_opened", { meal_type: mealType });
  setMealChoiceTarget({ dateKey, mealType });
  setShowMealChoiceSheet(true);
}

function closeMealChoiceSheet() {
  setShowMealChoiceSheet(false);
  setMealChoiceTarget(null);
}

// SUPABASE: add a meal to saved_meals
async function addSavedMeal(meal) {
  const userId = session?.user?.id;
  if (!userId) return;
  const row = { user_id: userId, name: meal.name, calories: meal.calories ?? null, protein: meal.macros?.protein ?? null, carbs: meal.macros?.carbs ?? null, fats: meal.macros?.fats ?? null };
  const { data, error } = await supabase.from("saved_meals").insert(row).select().single();
  if (!error && data) setSavedManualMeals(prev => [data, ...prev]);
}

// SUPABASE: remove a meal from saved_meals (optimistic)
async function deleteSavedMeal(id) {
  setSavedManualMeals(prev => prev.filter(m => m.id !== id));
  await supabase.from("saved_meals").delete().eq("id", id);
}

function closeMealSlotSheet() {
  setIsClosingMealSlotSheet(true);
  setTimeout(() => {
    setShowMealSlotSheet(false);
    setIsClosingMealSlotSheet(false);
    setMealSlotSheetTarget(null);
    setMealSlotSearch("");
    setMealSlotSelectedRecipe(null);
    setShowFavouritesOnly(false);
    setMealSheetExpanded(false);
    setMealSheetDragY(0);
  }, 220);
}

function getMealsPlannedThisWeek(offset) {
  const weekDays = getPlanCalendarDays(offset);
  return weekDays.reduce((total, date) => {
    const dateKey = getDateKey(date);
    const dayPlan = plan[dateKey] || {};
    return total + Object.values(dayPlan).filter(Boolean).length;
  }, 0);
}

function handleSheetDragStart(e) {
  const clientY = e.touches ? e.touches[0].clientY : e.clientY;
  mealSheetDragStartY.current = clientY;
  mealSheetIsDragging.current = true;
  setMealSheetDragY(0);
}

function handleSheetDragMove(e) {
  if (!mealSheetIsDragging.current) return;
  const clientY = e.touches ? e.touches[0].clientY : e.clientY;
  const delta = clientY - mealSheetDragStartY.current;
  setMealSheetDragY(delta);
}

function handleSheetDragEnd() {
  if (!mealSheetIsDragging.current) return;
  mealSheetIsDragging.current = false;

  if (!mealSheetExpanded && mealSheetDragY > 60) {
    setMealSheetExpanded(true);
  }

  setMealSheetDragY(0);
}


function applyMealToSlot(recipe) {
  if (!mealSlotSheetTarget) return;
  posthog.capture("meal_added_from_recipe", { recipe_name: recipe?.name });

  // Programme swap — update selectedProgramme week override only
  if (mealSlotSheetTarget.isProgrammeSwap) {
    const { weekIndex, day, mealType } = mealSlotSheetTarget;
    setSelectedProgramme((prev) => {
      const updatedWeeks = [...(prev.weeks || [])];
      updatedWeeks[weekIndex] = {
        ...updatedWeeks[weekIndex],
        days: {
          ...updatedWeeks[weekIndex].days,
          [day]: {
            ...updatedWeeks[weekIndex].days[day],
            [mealType]: recipe.name
          }
        }
      };
      return { ...prev, weeks: updatedWeeks };
    });
    setPlanToastMessage(`${recipe.name} swapped in for ${mealType}`);
    closeMealSlotSheet();
    return;
  }

  // Manual mode — existing logic below unchanged
  const { dateKey, mealType } = mealSlotSheetTarget;
  setPlan((prev) => ({
    ...prev,
    [dateKey]: {
      ...prev[dateKey],
      [mealType]: recipe
    }
  }));
  setPlanToastMessage(`${recipe.name} added to ${mealType}`);
  if (slotSelectQueue.length > 0) {
    const nextQueue = [...slotSelectQueue];
    const nextSlot = nextQueue.shift();
    setSlotSelectQueue(nextQueue);
    setMealSlotSelectedRecipe(null);
    setTimeout(() => {
      openMealSlotSheet(nextSlot.dateKey, nextSlot.mealType);
    }, 280);
  } else {
    closeMealSlotSheet();
  }
}

function removeAllWeeks() {
  setPlan({});
  setCompletedMeals({});
  setShowRemoveAllWeeksConfirm(false);
  setPlanToastMessage("All meals cleared.");
}

function saveMealTime() {
  const time = `${tempHour}:${tempMinute} ${tempPeriod}`;

  if (activeMealTimePicker === "breakfast") setBreakfastTime(time);
  if (activeMealTimePicker === "lunch") setLunchTime(time);
  if (activeMealTimePicker === "dinner") setDinnerTime(time);

  setActiveMealTimePicker(null);
}

async function sendSupportMessage() {
  if (!supportInput.trim() || supportIsTyping) return;

  const userMessage = supportInput.trim();
  setSupportInput("");
  setSupportError("");

  setSupportMessages((prev) => [
    ...prev,
    { role: "user", content: userMessage }
  ]);

  setSupportIsTyping(true);

  try {
    const systemPrompt = `You are a friendly and helpful support assistant for Bout Time, a meal planning app. 
    
Here is everything you know about the app:

- Bout Time has 5 main tabs: Home, Plan, Recipes, Shopping and Profile
- HOME TAB: Shows a carousel of days of the week. Each day card shows 3 meal slots (breakfast, lunch, dinner) as stacked cards. Tapping a day opens a meal viewer where you can swipe between meals.
- PLAN TAB: Has two modes — Manual and Programme. Manual mode lets users build their own week by adding meals to any slot. Programme mode follows a guided multi-week plan. Users can add weeks, shuffle meals, clear their plan and switch between weeks using arrow buttons.
- RECIPES TAB: Shows all recipes in a grid. Users can search, filter by tags (high protein, quick, low calorie, breakfast, lunch, dinner) and sort by name, time or calories. Tapping a recipe opens full details including macros, ingredients and instructions. Users can add recipes to their plan directly from here. There is a + button to add custom recipes.
- SHOPPING TAB: Automatically generates a shopping list from all meals in the current plan. Items are grouped by category (fresh produce, meat, fish, dairy, bakery, cupboard, frozen, other). Users can tick items off as they shop, add manual items and clear checked items.
- PROFILE TAB: Shows the user's profile, a Your Week summary with 7 day circles and stat pills, and a Consistency Calendar. Days where at least one meal was cooked show as orange. There is a streak counter showing consecutive days of cooking.
- SETTINGS: Accessed via the cog icon on the profile tab. Has Account (edit name, email, preferences), Appearance (light/dark/system), Notifications (toggles), Privacy, Support and Feedback pages.
- PROGRAMMES: Pre-built multi-week meal plans (e.g. 8 Week Fat Loss, 4 Week High Protein Reset, 2 Week Balanced Meals). Users can browse and pick a programme from the Plan tab. Individual meals within a programme can be swapped.
- RECIPES: Each recipe has a name, description, time, calories, servings, ingredients, instructions, macros (protein, carbs, fats), categories (breakfast/lunch/dinner) and tags. Default recipes are Chicken Stir Fry, Spaghetti Bolognese and Omelette.
- MEAL COMPLETION: Users mark meals as cooked using the Cooked checkbox in the Plan tab. This updates the streak and calendar.
- SHOPPING LIST: Ingredients come automatically from planned meals. Users can also add manual items. Items are grouped by category. Checked items can be cleared with the Clear Checked button.
- STREAK: The fire counter on the profile tab shows how many consecutive days the user has cooked at least one meal.

Keep answers concise, friendly and specific to Bout Time. If you genuinely cannot answer something, say so honestly and suggest they check the FAQ below or send a message via the feedback form. Never make up features that don't exist. Always refer to the app as Bout Time.`;

    const response = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1000,
        system: systemPrompt,
        messages: [
          ...supportMessages,
          { role: "user", content: userMessage }
        ]
      })
    });

    const data = await response.json();
    const reply = data.content
      .map((block) => (block.type === "text" ? block.text : ""))
      .join("");

    setSupportMessages((prev) => [
      ...prev,
      { role: "assistant", content: reply }
    ]);

  } catch (err) {
    setSupportError("Something went wrong — please try again.");
  } finally {
    setSupportIsTyping(false);
  }
}

 function addToPlan(day, mealType, recipe) {
  setPlan((prev) => ({
    ...prev,
    [day]: {
      ...prev[day],
      [mealType]: recipe
    }
  }));
}

function removeFromPlan(dateKey, mealType) {
  setPlan((prev) => ({
    ...prev,
    [dateKey]: {
      ...prev[dateKey],
      [mealType]: null
    }
  }));

  setCompletedMeals((prev) => {
    const updated = { ...prev };
    delete updated[`${dateKey}-${mealType}`];
    return updated;
  });
}

// addManualWeeks removed — manualWeeks was dead state (currentManualWeekIndex was permanently 0)
// Phase 5 will wire AddWeeksModal to meal_plan_entries
function handleAddWeeks() {
  setShowAddWeeksModal(false);
}

function openProgrammePicker() {
  setProgrammeSearch("");
  setShowProgrammePicker(true);
}

function chooseProgramme(programme) {
  posthog.capture("programme_started", { programme_name: programme?.name });
  setSelectedProgramme(programme);
  setProgrammeMealEditTarget(null);
  setCurrentProgrammeWeekIndex(0);
  setCompletedMeals({});
  setPlanningMode("programme");
  setShowProgrammePicker(false);
}

function quitProgramme() {
  // programmeHistory removed — was written-only, never displayed
  setSelectedProgramme(null);
  setPlanningMode("manual");
  setCurrentProgrammeWeekIndex(0);
  setCompletedMeals({});
}

function restartProgramme() {
  setCompletedMeals({});
  setCurrentProgrammeWeekIndex(0);
}

function switchProgrammeWeek(direction) {
  const totalWeeks = getProgrammeWeekCount(selectedProgramme);

  setWeekTransitionDirection(direction >= 0 ? "forward" : "backward");

  setCurrentProgrammeWeekIndex((prevIndex) => {
    let nextIndex = prevIndex + direction;

    if (nextIndex < 0) nextIndex = 0;
    if (nextIndex >= totalWeeks) nextIndex = totalWeeks - 1;

    return nextIndex;
  });
}

function goToProgrammeWeek(index) {
  const totalWeeks = getProgrammeWeekCount(selectedProgramme);
  const safeIndex = Math.max(0, Math.min(index, totalWeeks - 1));

  setWeekTransitionDirection(
    safeIndex >= currentProgrammeWeekIndex ? "forward" : "backward"
  );

  setCurrentProgrammeWeekIndex(safeIndex);
}

function getRecipeByName(recipeName) {
  if (!recipeName) return null;

  return (
    recipes.find(
      (recipe) => recipe.name.toLowerCase() === recipeName.toLowerCase()
    ) || null
  );
}

function handleChangeProfileCalendarMonth(direction) {
  setProfileCalendarDate((prevDate) =>
    changeProfileCalendarMonth(prevDate, direction)
  );
}


function handleProfileImageChange(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  const reader = new FileReader();

  reader.onload = () => {
    if (typeof reader.result === "string") {
      setProfileImage(reader.result);
      setShowProfilePhotoActions(false);
      const userId = session?.user?.id;
      if (userId) {
        supabase.from("user_profiles")
          .upsert({ id: userId, profile_image: reader.result }, { onConflict: "id" })
          .then(({ error }) => { if (error) console.error("profileImage save:", error); });
      }
    }
  };

  reader.readAsDataURL(file);
}

function saveProfileImage(base64) {
  setProfileImage(base64);
  const userId = session?.user?.id;
  if (userId) {
    supabase.from("user_profiles")
      .upsert({ id: userId, profile_image: base64 }, { onConflict: "id" })
      .then(({ error }) => { if (error) console.error("profileImage save:", error); });
  }
}

function removeProfileImage() {
  setProfileImage("");
  setShowProfilePhotoActions(false);
  const userId = session?.user?.id;
  if (userId) {
    supabase.from("user_profiles")
      .upsert({ id: userId, profile_image: null }, { onConflict: "id" })
      .then(({ error }) => { if (error) console.error("profileImage remove:", error); });
  }
}

function openProgrammeRecipe(recipeName) {
  const recipe = getRecipeByName(recipeName);
  if (!recipe) return;

  setSelectedHomeDay(null);
  setSelectedRecipe(recipe);
  setRecipeModalView("details");
  setRecipeModalDirection("forward");
}

function startProgrammeMealSwap(weekIndex, day, mealType) {
  setMealSlotSheetTarget({
    dateKey: `programme-week${weekIndex}-${day}`,
    mealType,
    isProgrammeSwap: true,
    weekIndex,
    day
  });
  setMealSlotSelectedRecipe(null);
  setMealSlotSearch("");
  setShowMealSlotSheet(true);
}

function applyRecipeToProgrammeMeal(recipe) {
  if (!recipe || !selectedProgramme || !programmeMealEditTarget) return;

  const updatedProgramme = replaceProgrammeMeal(
    selectedProgramme,
    programmeMealEditTarget,
    recipe
  );

  setSelectedProgramme(updatedProgramme);

  setPlanToastMessage(
    `${recipe.name} added to ${programmeMealEditTarget.day} ${programmeMealEditTarget.mealType}`
  );

  setProgrammeMealEditTarget(null);
  setActiveTab("plan");
  closeRecipeModal();
}

function viewSelectedProgramme() {
  if (!selectedProgramme) return;
  setSelectedProgrammeDetail(selectedProgramme);
}

function openProgrammeDetails(programme) {
  setSelectedProgrammeDetail(programme);
}

function closeProgrammeDetails() {
  setSelectedProgrammeDetail(null);
}

function clearEntirePlan() {
  const weekDays = getPlanCalendarDays(planCalendarWeekOffset);

  setPlan((prevPlan) => {
    const updatedPlan = { ...prevPlan };
    weekDays.forEach((date) => {
      const dateKey = getDateKey(date);
      updatedPlan[dateKey] = { breakfast: null, lunch: null, dinner: null };
    });
    return updatedPlan;
  });

  setCompletedMeals({});
  setShowClearPlanConfirm(false);
  setPlanToastMessage("Week cleared.");
}

  const closeRecipeModal = useCallback(() => {
  if (mealSlotSheetTarget) {
    setShowMealSlotSheet(true);
  }
  setSelectedRecipe(null);
  setSelectedHomeDay(null);
  setHomeMealIndex(0);
  setRecipeModalView("details");
  setRecipeModalDirection("forward");
  setSelectedPlanSlots([]);
  setDragOffset(0);
  setIsFullPageRecipe(false);
  setRecipeFullPageContext(null);
  isDraggingRef.current = false;
  deltaXRef.current = 0;
}, [mealSlotSheetTarget]);

function openCookingMode(recipe, context = null) {
  setCookingModeRecipe(recipe);
  setCookingModeContext(context);
}

function closeCookingMode() {
  setCookingModeRecipe(null);
  setCookingModeContext(null);
}

function openRecipeDetails(recipe, fullPage = false, context = null) {
  posthog.capture("recipe_viewed", { recipe_name: recipe?.name });
  setSelectedHomeDay(null);
  setSelectedRecipe(recipe);
  setRecipeModalView("details");
  setRecipeModalDirection("forward");
  setSelectedPlanSlots([]);
  setIsFullPageRecipe(fullPage);
  setRecipeFullPageContext(context);
}

function openRecipeQuickAdd(recipe) {
  setSelectedHomeDay(null);
  setSelectedRecipe(recipe);
  setRecipeModalView("plan");
  setRecipeModalDirection("forward");
  setSelectedPlanSlots([]);
}

function toggleRecipePlanSlot(day, mealType) {
  const slotKey = `${day}::${mealType}`;

  setSelectedPlanSlots((prev) => {
    const alreadySelected = prev.some(
      (slot) => slot.day === day && slot.mealType === mealType
    );

    if (alreadySelected) {
      return prev.filter(
        (slot) => `${slot.day}::${slot.mealType}` !== slotKey
      );
    }

    return [...prev, { day, mealType }];
  });
}

function addRecipeToPlanSelectedSlots(recipe) {
  if (!recipe || planSelectedSlots.length === 0) return;

  const slotsToAdd = [...planSelectedSlots];

  setPlan((prevPlan) => {
    const updatedPlan = { ...prevPlan };

    slotsToAdd.forEach(({ day, mealType }) => {
      updatedPlan[day] = {
        ...updatedPlan[day],
        [mealType]: recipe
      };
    });

    return updatedPlan;
  });

  if (slotsToAdd.length === 1) {
    setPlanToastMessage(
      `${recipe.name} added to ${slotsToAdd[0].day} ${slotsToAdd[0].mealType}`
    );
  } else if (slotsToAdd.length <= 3) {
    const labels = slotsToAdd.map(
      (slot) => `${slot.day} ${slot.mealType}`
    );

    setPlanToastMessage(
      `${recipe.name} added to ${labels.join(", ")}`
    );
  } else {
    setPlanToastMessage(
      `${recipe.name} added to ${slotsToAdd.length} meals`
    );
  }
  setPlanSelectedSlots([]);
  setActiveTab("plan");
  closeRecipeModal();
}

function confirmAddSelectedRecipeToPlan() {
  if (!selectedRecipe || selectedPlanSlots.length === 0) return;

  const slotsToAdd = [...selectedPlanSlots];

  setPlan((prevPlan) => {
    const updatedPlan = { ...prevPlan };

    slotsToAdd.forEach(({ day, mealType }) => {
      updatedPlan[day] = {
        ...updatedPlan[day],
        [mealType]: selectedRecipe
      };
    });

    return updatedPlan;
  });

  if (slotsToAdd.length === 1) {
    setPlanToastMessage(
      `${selectedRecipe.name} added to ${slotsToAdd[0].day} ${slotsToAdd[0].mealType}`
    );
  } else if (slotsToAdd.length <= 3) {
    const labels = slotsToAdd.map(
      (s) => `${s.day} ${s.mealType}`
    );

    setPlanToastMessage(
      `${selectedRecipe.name} added to ${labels.join(", ")}`
    );
  } else {
    setPlanToastMessage(
      `${selectedRecipe.name} added to ${slotsToAdd.length} meals`
    );
  }

  closeRecipeModal();
}

function goToRecipePlanView() {
  setRecipeModalDirection("forward");
  setRecipeModalView("plan");
}

function goToRecipeDetailsView() {
  setRecipeModalDirection("back");
  setRecipeModalView("details");
}

function handleEditSelectedRecipe() {
  if (!selectedRecipe?.custom) return;

  // Keep recipe modal open underneath so cancelling edit returns to it
  startEditingRecipe(selectedRecipe);
}

function handleDeleteSelectedRecipe() {
  if (!selectedRecipe?.custom) return;

  setRecipePendingDelete(selectedRecipe);
  setShowDeleteRecipeConfirm(true);
}

function confirmDeleteSelectedRecipe() {
  if (!recipePendingDelete) return;

  deleteRecipe(recipePendingDelete);
  setRecipePendingDelete(null);
  setShowDeleteRecipeConfirm(false);
  closeRecipeModal();
}

function closeDeleteRecipeConfirm() {
  setRecipePendingDelete(null);
  setShowDeleteRecipeConfirm(false);
}

function completeMeal(dateKey, mealType) {
  const isNowCooked = !completedMeals[`${dateKey}-${mealType}`];
  const newCompletedMeals = {
    ...completedMeals,
    [`${dateKey}-${mealType}`]: isNowCooked,
  };
  setCompletedMeals(newCompletedMeals);

  // SUPABASE: persist date-keyed completions (skip programme week keys)
  const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
  const userId = session?.user?.id;
  if (userId && ISO_DATE_RE.test(dateKey)) {
    if (isNowCooked) {
      supabase.from("completed_meals")
        .insert({ user_id: userId, date: dateKey, meal_type: mealType })
        .then(({ error }) => { if (error) console.error("completeMeal insert:", error); });
    } else {
      supabase.from("completed_meals").delete()
        .eq("user_id", userId).eq("date", dateKey).eq("meal_type", mealType)
        .then(({ error }) => { if (error) console.error("completeMeal delete:", error); });
    }
  }

  // Check programme completion if in programme mode
  if (planningMode === "programme" && selectedProgramme) {
    if (!isNowCooked) return; // only check when marking as cooked, not uncooking

    // Check if all programme meals are now cooked
    // We use the updated completedMeals object for this check
    const updatedCompleted = {
      ...newCompletedMeals
    };

    // For programme mode we check against the days array
    const programmeWeeks = selectedProgramme.weeks || [];
    const totalDays = days; // Mon-Sun array

    // Simple check: is this the last day of the last week?
    const totalWeeks = programmeWeeks.length;
    if (currentProgrammeWeekIndex === totalWeeks - 1) {
      const lastWeek = programmeWeeks[totalWeeks - 1];
      const lastWeekDays = lastWeek?.days || {};
      const lastDay = totalDays[totalDays.length - 1]; // Sunday
      const lastDayMeals = lastWeekDays[lastDay] || {};
      const lastDayMealTypes = Object.keys(lastDayMeals);

      if (lastDayMealTypes.length > 0) {
        const allLastDayCooked = lastDayMealTypes.every(
        (mt) => updatedCompleted[`week${totalWeeks - 1}-${lastDay}-${mt}`]
      );

        if (allLastDayCooked) {
          setShowProgrammeSuccessModal(true);
        }
      }
    }
  }
}

  function toggleItem(item) {
    setCheckedItems((prev) => ({
      ...prev,
      [item]: !prev[item]
    }));
  }

  function addManualItem() {
    if (!newItem.trim()) return;

    setManualItems((prev) => {
      if (prev.includes(newItem.trim())) return prev;
      return [...prev, newItem.trim()];
    });

    setNewItem("");
  }

  function addNamedManualItem(name) {
    const trimmed = name.trim();
    if (!trimmed) return;
    setManualItems((prev) => prev.includes(trimmed) ? prev : [...prev, trimmed]);
  }

  function removeManualItem(itemToRemove) {
    setManualItems((prev) => prev.filter((item) => item !== itemToRemove));

    setCheckedItems((prev) => {
      const updated = { ...prev };
      delete updated[itemToRemove];
      return updated;
    });
  }

 function deleteRecipe(recipeToDelete) {
  // SUPABASE: delete from custom_recipes by UUID if available
  if (recipeToDelete.id && recipeToDelete.custom) {
    supabase.from("custom_recipes").delete().eq("id", recipeToDelete.id)
      .then(({ error }) => { if (error) console.error("deleteRecipe:", error); });
  }
  setRecipes(prev => prev.filter(r => r.name !== recipeToDelete.name));
  setPlan(prev => removeRecipeFromPlan(prev, recipeToDelete.name));
}

function toggleFilter(filterValue) {
  setActiveFilters((prev) =>
    prev.includes(filterValue)
      ? prev.filter((value) => value !== filterValue)
      : [...prev, filterValue]
  );
}

function clearAllFilters() {
  setActiveFilters([]);
}

function applyFilters(filters) {
  setActiveFilters(filters);
}

function saveEditedRecipe() {
  if (
    !editRecipeName.trim() ||
    !editRecipeCategory ||
    editRecipeIngredients.length === 0
  ) {
    return;
  }

  const built = buildRecipeFromForm({
    name: editRecipeName,
    image: editRecipeImage,
    description: editRecipeDescription,
    time: editRecipeTime,
    calories: editRecipeCalories,
    servings: editRecipeServings,
    category: editRecipeCategory,
    tag: editRecipeTag,
    protein: editRecipeProtein,
    carbs: editRecipeCarbs,
    fats: editRecipeFats,
    instructions: editRecipeInstructions,
    ingredients: editRecipeIngredients,
    custom: true,
  });

  // Preserve the Supabase UUID so future edits/deletes can use it
  const existing = recipes.find(r => r.name === editingRecipeName && r.custom);
  const updatedRecipe = existing?.id ? { ...built, id: existing.id } : built;

  // Optimistic local update
  setRecipes(prev => prev.map(r => r.name === editingRecipeName ? updatedRecipe : r));
  setPlan(prev => replaceRecipeInPlan(prev, editingRecipeName, updatedRecipe));
  setSelectedRecipe(updatedRecipe);
  cancelEditingRecipe();

  // SUPABASE: persist update in background
  const userId = session?.user?.id;
  if (existing?.id && userId) {
    supabase.from("custom_recipes")
      .update(appRecipeToDbCustom(updatedRecipe, userId))
      .eq("id", existing.id)
      .then(({ error }) => { if (error) console.error("saveEditedRecipe:", error); });
  }
}

function addRecipe() {
  if (
    !newRecipeName.trim() ||
    !newRecipeCategory ||
    newRecipeIngredients.length === 0
  ) {
    return;
  }

  const recipe = buildRecipeFromForm({
    name: newRecipeName,
    image: newRecipeImage,
    description: newRecipeDescription,
    time: newRecipeTime,
    calories: newRecipeCalories,
    servings: newRecipeServings,
    category: newRecipeCategory,
    tag: newRecipeTag,
    protein: newRecipeProtein,
    carbs: newRecipeCarbs,
    fats: newRecipeFats,
    instructions: newRecipeInstructions,
    ingredients: newRecipeIngredients,
    custom: true,
  });

  // Optimistic: add locally and close form immediately
  setRecipes(prev => [...prev, recipe]);
  clearAddRecipeForm();

  // SUPABASE: persist in background, then attach UUID to local state
  const userId = session?.user?.id;
  if (userId) {
    supabase.from("custom_recipes")
      .insert(appRecipeToDbCustom(recipe, userId))
      .select().single()
      .then(({ data, error }) => {
        if (!error && data) {
          // Replace the optimistic entry with the Supabase-backed version (has UUID)
          setRecipes(prev => prev.map(r =>
            r === recipe || (r.name === recipe.name && r.custom && !r.id)
              ? dbRecipeToApp(data, true)
              : r
          ));
        }
      });
  }
}

  const recipeItems = useMemo(() => {
    const mealIngredients = Object.values(plan)
      .flatMap((dayPlan) => (dayPlan ? Object.values(dayPlan) : []))
      .filter((item) => item !== null && !Array.isArray(item) && item.ingredients)
      .flatMap((item) => {
        const mult = item.servingsOverride ?? servingSizePreference ?? 1;
        return item.ingredients.map((ing) => {
          const raw = (typeof ing === "object" && ing !== null)
            ? [ing.quantity, ing.name].filter(Boolean).join(" ")
            : ing;
          return mult === 1 ? raw : applyIngredientMultiplier(raw, mult);
        });
      })
      .filter((ingredient) => ingredient && typeof ingredient === "string");

    const snackIngredients = Object.values(plan)
      .flatMap((dayPlan) => (dayPlan?.snacks || []))
      .filter((snack) => snack.mode === "plan" && snack.ingredients)
      .flatMap((snack) => snack.ingredients.map(ing =>
        (typeof ing === "object" && ing !== null)
          ? [ing.quantity, ing.name].filter(Boolean).join(" ")
          : ing
      ))
      .filter((ingredient) => ingredient && typeof ingredient === "string");

    return mergeShoppingIngredients([...mealIngredients, ...snackIngredients]);
  }, [plan, servingSizePreference]);

  const shoppingList = useMemo(() => {
    return [...new Set([...recipeItems, ...manualItems])];
  }, [recipeItems, manualItems]);

  const allStapleNames = useMemo(() =>
    [...defaultKitchenStaples.filter(s => !removedDefaultStaples.includes(s)), ...customStaples].map(s => s.toLowerCase()),
  [customStaples, removedDefaultStaples]);

  const groupedShoppingList = useMemo(() =>
    groupShoppingItems(shoppingList, allStapleNames),
  [shoppingList, allStapleNames]);

  const stapleItems = useMemo(() =>
    shoppingList.filter((item) => {
      if (!item || typeof item !== "string") return false;
      const lower = item.toLowerCase().trim();
      return allStapleNames.some(s => lower.includes(s) || s.includes(lower));
    }),
  [shoppingList, allStapleNames]);

const orderedHomeMeals = selectedHomeDay
  ? getMealsForDayInFixedOrder(plan, completedMeals, selectedHomeDay)
  : [];

const currentHomeMealItem =
  selectedHomeDay && orderedHomeMeals.length > 0
    ? orderedHomeMeals[homeMealIndex]
    : null;

const activeModalRecipe = selectedHomeDay
  ? currentHomeMealItem?.meal || null
  : selectedRecipe;

const activeModalMealType = selectedHomeDay
  ? currentHomeMealItem?.mealType || null
  : null;

const isRecipeDetailsModalOpen = !!(selectedRecipe && !selectedHomeDay);
const isHomeDayModalOpen = !!selectedHomeDay;
const isAnyModalOpen =
  isRecipeDetailsModalOpen ||
  isHomeDayModalOpen ||
  showAddWeeksModal ||
  showProgrammePicker ||
  !!selectedProgrammeDetail ||
  showAddRecipeForm ||
  !!editingRecipeName ||
  showSettingsModal ||
  !!activeMealTimePicker ||
  showMealSlotSheet ||
  showMealChoiceSheet ||
  showFullWeek ||
  isFullPageRecipe ||
  !!cookingModeRecipe ||
  showManualEntryModal ||
  showProgrammeSuccessModal;

useEffect(() => {
  if (!isAnyModalOpen) return;

  const originalBodyOverflow = document.body.style.overflow;
  const originalBodyTouchAction = document.body.style.touchAction;
  const originalHtmlOverflow = document.documentElement.style.overflow;

  document.body.style.overflow = "hidden";
  document.documentElement.style.overflow = "hidden";

  return () => {
    document.body.style.overflow = originalBodyOverflow;
    document.body.style.touchAction = originalBodyTouchAction;
    document.documentElement.style.overflow = originalHtmlOverflow;
  };
}, [isAnyModalOpen]);

useEffect(() => {
  const hasDayNameKeys = Object.keys(plan).some(key =>
    days.includes(key)
  );

  if (hasDayNameKeys) {
    const monday = getPlanCalendarWeekStart(0);
    const migratedPlan = {};

    days.forEach((dayName, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);
      const dateKey = getDateKey(date);
      if (plan[dayName]) {
        migratedPlan[dateKey] = plan[dayName];
      }
    });

    setPlan(migratedPlan);
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

useEffect(() => {
  if (!planToastMessage) return;

  const timeout = setTimeout(() => {
    setPlanToastMessage("");
  }, 2800);

  return () => clearTimeout(timeout);
}, [planToastMessage]);

useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth <= 768);

    requestAnimationFrame(() => {
      updateHomeCarouselVisuals();
    });
  };

  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, [updateHomeCarouselVisuals]);

  useEffect(() => {
    setCheckedItems((prev) => {
      const updated = {};
      shoppingList.forEach((item) => {
        updated[item] = prev[item] || false;
      });
      return updated;
    });
  }, [shoppingList, setCheckedItems]);

useEffect(() => {
  if (tabContentRef.current) {
    tabContentRef.current.scrollTop = 0;
  }
  window.scrollTo(0, 0);
}, [activeTab]);

  // MIGRATED: plan now syncs to Supabase meal_plan_entries.
  // Debounced 500ms so rapid changes (adding multiple meals) send one write.
  // planInitialisedRef prevents firing during the initial Supabase load.
  useEffect(() => {
    if (!planInitialisedRef.current) {
      console.log("[plan sync] skipped — planInitialisedRef is false (initial load in progress)");
      return;
    }
    const userId = session?.user?.id;
    if (!userId) {
      console.log("[plan sync] skipped — no userId");
      return;
    }
    console.log("[plan sync] effect fired, debouncing 500ms...");

    const timer = setTimeout(() => {
      const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
      const rows = [];
      Object.entries(plan).forEach(([date, dayPlan]) => {
        // Skip any legacy day-name keys ("Monday" etc.) that may still be in state
        // from old localStorage data — they would cause a 400 on the date column.
        if (!dayPlan || !ISO_DATE_RE.test(date)) return;
        ["breakfast", "lunch", "dinner"].forEach(mealType => {
          const recipe = dayPlan[mealType];
          if (!recipe) return;
          // All rows have the same column set so PostgREST receives a uniform batch.
          rows.push({
            user_id:          userId,
            date,
            meal_type:        mealType,
            recipe_id:        (!recipe.custom && recipe.id) ? recipe.id : null,
            custom_recipe_id: (recipe.custom  && recipe.id) ? recipe.id : null,
            meal_name:        recipe.name || null,
            snack_data:       null,
          });
        });
        const snacks = dayPlan.snacks || [];
        if (snacks.length > 0) {
          rows.push({
            user_id:          userId,
            date,
            meal_type:        "snacks",
            recipe_id:        null,
            custom_recipe_id: null,
            meal_name:        null,
            snack_data:       snacks,
          });
        }
      });

      console.log("[plan sync] writing", rows.length, "rows to meal_plan_entries:", rows);

      // Delete all then re-insert — reliable for any change type (add, remove, clear, generate).
      supabase.from("meal_plan_entries").delete().eq("user_id", userId)
        .then(({ error }) => {
          if (error) { console.error("[plan sync] delete error:", error); return; }
          console.log("[plan sync] delete ok, inserting", rows.length, "rows");
          if (rows.length > 0) {
            supabase.from("meal_plan_entries").insert(rows).select()
              .then(({ data, error: e, status, statusText }) => {
                if (e) {
                  console.error("[plan sync] insert error — status:", status, statusText);
                  console.error("[plan sync] insert error — message:", e?.message, "| code:", e?.code, "| details:", e?.details, "| hint:", e?.hint);
                  console.error("[plan sync] insert error — payload sample:", JSON.stringify(rows[0]));
                } else {
                  console.log("[plan sync] insert succeeded — status:", status, "| rows confirmed in response:", data?.length ?? "none returned");
                }
              })
              .catch(err => {
                console.error("[plan sync] insert promise rejected (unhandled):", err?.message ?? String(err));
                console.error("[plan sync] payload that caused rejection:", JSON.stringify(rows[0]));
              });
          }
        });
    }, 500);

    return () => {
      console.log("[plan sync] cleanup — cancelling pending 500ms timer (userId was:", userId, ")");
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, session?.user?.id]);

  // MIGRATED: recipes localStorage persistence removed — source of truth is now Supabase

  // manualWeeks persistence removed — manualWeeks was dead state
  useEffect(() => {
    // Load active programme from localStorage on mount
    try {
      const savedProgramme = localStorage.getItem("selectedProgramme");
      const savedWeekIndex = localStorage.getItem("programmeWeekIndex");
      if (savedProgramme) setSelectedProgramme(JSON.parse(savedProgramme));
      if (savedWeekIndex !== null) setCurrentProgrammeWeekIndex(Number(savedWeekIndex));
    } catch (err) {
      console.error("Failed to load programme state", err);
    }
  }, []);


useEffect(() => {
  try {
    if (selectedProgramme) {
      localStorage.setItem(
        "selectedProgramme",
        JSON.stringify(selectedProgramme)
      );
    } else {
      localStorage.removeItem("selectedProgramme");
    }

    localStorage.setItem(
      "programmeWeekIndex",
      currentProgrammeWeekIndex
    );

    localStorage.setItem("planningMode", planningMode);
  } catch (err) {
    console.error("Failed to save programme state", err);
  }
}, [selectedProgramme, currentProgrammeWeekIndex, planningMode]);

// lastActiveAt effect removed — the inactiveTooLong value it enabled was never acted on

useEffect(() => {
  function handleClickOutside(event) {
    if (
      profilePhotoActionsRef.current &&
      !profilePhotoActionsRef.current.contains(event.target)
    ) {
      setShowProfilePhotoActions(false);
    }
  }

  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [setShowProfilePhotoActions]);

const todayIndex = new Date().getDay();
  const today = days[todayIndex === 0 ? 6 : todayIndex - 1];

useLayoutEffect(() => {
  if (activeTab !== "home") return;
  if (!homeDaysCarouselRef.current) return;

  const container = homeDaysCarouselRef.current;

  const positionTodayCard = () => {
    const todayCard = container.querySelector(`[data-day="${today}"]`);
    if (!todayCard) return false;

    const left =
      todayCard.offsetLeft -
      (container.clientWidth / 2 - todayCard.clientWidth / 2);

    container.scrollLeft = left;
    return true;
  };

  const frame1 = requestAnimationFrame(() => {
    const didPosition = positionTodayCard();

    const frame2 = requestAnimationFrame(() => {
      if (didPosition) {
        positionTodayCard();
      }
      updateHomeCarouselVisuals();
    });

    container.__homeFrame2 = frame2;
  });

  container.__homeFrame1 = frame1;
  const carouselFrame = homeCarouselFrameRef.current;

  return () => {
    if (container.__homeFrame1) cancelAnimationFrame(container.__homeFrame1);
    if (container.__homeFrame2) cancelAnimationFrame(container.__homeFrame2);
    if (carouselFrame) cancelAnimationFrame(carouselFrame);
  };
}, [today, activeTab, isMobile, updateHomeCarouselVisuals]);

useEffect(() => {
  function handleEscapeKey(event) {
    if (event.key === "Escape") {
            if (showProfilePhotoActions) {
        setShowProfilePhotoActions(false);
        return;
      }
            if (showSettingsModal) {
        closeSettingsModal();
        return;
      }
            if (selectedProgrammeDetail) {
        closeProgrammeDetails();
        return;
      }
            if (showProgrammePicker) {
        setShowProgrammePicker(false);
        return;
      }
      if (showAddWeeksModal) {
        setShowAddWeeksModal(false);
        return;
      }

      if (showAddRecipeForm) {
        clearAddRecipeForm();
        setShowAddRecipeForm(false);
        return;
      }

      if (editingRecipeName) {
        cancelEditingRecipe();
        return;
      }

      if (selectedRecipe || selectedHomeDay) {
        closeRecipeModal();
      }
    }
  }

  window.addEventListener("keydown", handleEscapeKey);

  return () => {
    window.removeEventListener("keydown", handleEscapeKey);
  };
}, [
  showProfilePhotoActions,
  showSettingsModal,
  closeSettingsModal,
  selectedProgrammeDetail,
  showProgrammePicker,
  showAddWeeksModal,
  showAddRecipeForm,
  editingRecipeName,
  selectedRecipe,
  selectedHomeDay,
  closeRecipeModal,
  clearAddRecipeForm,
  cancelEditingRecipe,
  setShowProfilePhotoActions
]);

const recipesTabFilteredRecipes = recipes
  .filter((recipe) => {
    if (recipesShowFavouritesOnly && !favouriteRecipes.includes(recipe.name)) return false;
    if (recipesShowMyRecipesOnly && !recipe.custom) return false;

    const matchesSearch = recipe.name
      .toLowerCase()
      .includes(recipeSearch.toLowerCase());

    if (activeFilters.length === 0) {
      return matchesSearch;
    }

    const matchesFilter = activeFilters.every((filter) =>
      recipe.tags.includes(filter) || recipe.categories.includes(filter)
    );

    return matchesSearch && matchesFilter;
  })
  .sort((a, b) => {
    if (sortBy === "name-asc") {
      return a.name.localeCompare(b.name);
    }

    if (sortBy === "time-asc") {
      return (a.time || 0) - (b.time || 0);
    }

    if (sortBy === "calories-asc") {
      return (a.calories || 0) - (b.calories || 0);
    }

    return 0;
  });


const currentProgrammeWeek = getCurrentProgrammeWeek(
  selectedProgramme,
  currentProgrammeWeekIndex
);

const currentProgrammeProgress = selectedProgramme
  ? (() => {
      const programmeWeeks = selectedProgramme.weeks || [];
      const totalWeeks = programmeWeeks.length;
      if (totalWeeks === 0) return 0;

      let totalMeals = 0;
      let totalCooked = 0;

      programmeWeeks.forEach((week, weekIndex) => {
        const weekDays = week?.days || {};
        Object.entries(weekDays).forEach(([day, dayMeals]) => {
          Object.keys(dayMeals || {}).forEach((mealType) => {
            totalMeals++;
            if (completedMeals[`week${weekIndex}-${day}-${mealType}`]) {
              totalCooked++;
            }
          });
        });
      });

      return totalMeals > 0 ? Math.round((totalCooked / totalMeals) * 100) : 0;
    })()
  : 0;


const appTheme = getBrandTheme();
const overlayStyle = modalOverlayStyle(appTheme, isMobile);
const contentStyle = modalContentStyle(appTheme, isMobile);

const profileCalendarMonthLabel = profileCalendarDate.toLocaleString("en-GB", {
  month: "long",
  year: "numeric"
});

const profileCalendarYear = profileCalendarDate.getFullYear();
const profileCalendarMonth = profileCalendarDate.getMonth();

const profileCalendarFirstDay = new Date(
  profileCalendarYear,
  profileCalendarMonth,
  1
);

const profileCalendarDaysInMonth = new Date(
  profileCalendarYear,
  profileCalendarMonth + 1,
  0
).getDate();

const profileCalendarStartOffset =
  (profileCalendarFirstDay.getDay() + 6) % 7;

const todayDate = new Date();

const currentStreak = calculateStreakFromCompletedMeals(completedMeals);

// Calendar date-completed check — uses Supabase-backed completedMeals, replacing
// the broken completionHistory approach (completionHistory was never written to).
function checkDateCompleted(dateKey) {
  return isDateCompletedFromMeals(completedMeals, dateKey);
}

// Wraps setDietaryPreference so every change is synced to user_profiles.
function setAndSyncDietaryPreference(value) {
  setDietaryPreference(value);
  const userId = session?.user?.id;
  if (!userId) return;
  supabase.from("user_profiles")
    .upsert({ id: userId, dietary_preference: value }, { onConflict: "id" })
    .then(({ error }) => { if (error) console.error("dietaryPreference sync:", error); });
}

// Note save/delete wrappers — update state AND write to Supabase.
function handleCreateManualPlan() {
  setHasCreatedManualPlan(true);
  const userId = session?.user?.id;
  if (userId) {
    supabase.from("user_profiles")
      .upsert({ id: userId, has_created_manual_plan: true }, { onConflict: "id" })
      .then(({ error }) => { if (error) console.error("hasCreatedManualPlan save:", error); });
  }
}

function handleChooseProgrammeFlag() {
  setHasChosenProgramme(true);
  const userId = session?.user?.id;
  if (userId) {
    supabase.from("user_profiles")
      .upsert({ id: userId, has_chosen_programme: true }, { onConflict: "id" })
      .then(({ error }) => { if (error) console.error("hasChosenProgramme save:", error); });
  }
}

function saveDailyNote(dateKey, note, source = "manual") {
  if (note) {
    setDailyNotes(prev => ({ ...prev, [dateKey]: note }));
    const userId = session?.user?.id;
    if (userId) {
      supabase.from("daily_notes")
        .upsert({ user_id: userId, date: dateKey, note, source }, { onConflict: "user_id,date" })
        .then(({ error }) => { if (error) console.error("saveDailyNote:", error); });
    }
  } else {
    setDailyNotes(prev => { const u = { ...prev }; delete u[dateKey]; return u; });
    const userId = session?.user?.id;
    if (userId) {
      supabase.from("daily_notes").delete()
        .eq("user_id", userId).eq("date", dateKey)
        .then(({ error }) => { if (error) console.error("deleteDailyNote:", error); });
    }
  }
}

function saveRecipeNote(recipeName, note) {
  if (note) {
    setRecipeNotes(prev => ({ ...prev, [recipeName]: note }));
    const userId = session?.user?.id;
    if (userId) {
      supabase.from("recipe_notes")
        .upsert({ user_id: userId, recipe_name: recipeName, note }, { onConflict: "user_id,recipe_name" })
        .then(({ error }) => { if (error) console.error("saveRecipeNote:", error); });
    }
  } else {
    setRecipeNotes(prev => { const u = { ...prev }; delete u[recipeName]; return u; });
    const userId = session?.user?.id;
    if (userId) {
      supabase.from("recipe_notes").delete()
        .eq("user_id", userId).eq("recipe_name", recipeName)
        .then(({ error }) => { if (error) console.error("deleteRecipeNote:", error); });
    }
  }
}

const profileCalendarCells = [
  ...Array(profileCalendarStartOffset).fill(null),
  ...Array.from(
    { length: profileCalendarDaysInMonth },
    (_, index) => index + 1
  )
];

function toggleFavourite(recipeName) {
  const isFav = favouriteRecipes.includes(recipeName);

  // Optimistic local update
  setFavouriteRecipes(prev => {
    if (!isFav) posthog.capture("recipe_saved", { recipe_name: recipeName });
    return toggleFavouriteRecipe(prev, recipeName);
  });

  // SUPABASE: write to favourite_recipes in background
  const userId = session?.user?.id;
  if (!userId) return;
  const recipe = recipes.find(r => r.name === recipeName);
  if (!recipe?.id) return; // recipe not yet persisted to Supabase
  const col = recipe.custom ? "custom_recipe_id" : "recipe_id";
  if (isFav) {
    supabase.from("favourite_recipes").delete()
      .eq("user_id", userId).eq(col, recipe.id)
      .then(({ error }) => { if (error) console.error("toggleFavourite delete:", error); });
  } else {
    supabase.from("favourite_recipes")
      .insert({ user_id: userId, [col]: recipe.id })
      .then(({ error }) => { if (error) console.error("toggleFavourite insert:", error); });
  }
}

function closeHomeDayViewer() {
  setSelectedHomeDay(null);
  setHomeMealIndex(0);
  setDragOffset(0);
  isDraggingRef.current = false;
  deltaXRef.current = 0;
  setSelectedRecipe(null);
}

function goToHomeViewerMeal(index) {
  if (!selectedHomeDay) return;

  const fixedMeals = getMealsForDayInFixedOrder(plan, completedMeals, selectedHomeDay);
  const safeIndex =
    ((index % fixedMeals.length) + fixedMeals.length) % fixedMeals.length;

  setHomeMealIndex(safeIndex);
  setSelectedRecipe(fixedMeals[safeIndex]?.meal || null);
}

function handleChooseRecipeFromHomeModal() {
  if (!selectedHomeDay || !activeModalMealType) return;

  setRecipeSearch("");
  closeHomeDayViewer();
}

function handleQuickAddFromHomeModal() {
  if (!selectedHomeDay || !activeModalMealType) return;

  const mealName = window.prompt(
    `Quick add a ${activeModalMealType} for ${selectedHomeDay}:`
  );

  if (!mealName || !mealName.trim()) return;

  const quickMeal = {
    name: mealName.trim(),
    description: "Quick added meal",
    image: "",
    time: 10,
    calories: 0,
    servings: 1,
    categories: [activeModalMealType],
    tags: ["quick"],
    macros: {
      protein: 0,
      carbs: 0,
      fats: 0
    },
    ingredients: [],
    instructions: "Add cooking steps here later.",
  };

  addToPlan(selectedHomeDay, activeModalMealType, quickMeal);
  setSelectedRecipe(quickMeal);
}

function handleTouchStart(e) {
  handleHomeTouchStart(e, dragRefs);
}

function handleTouchMove(e) {
  handleHomeTouchMove(e, dragRefs, setDragOffset);
}

function handleTouchEnd() {
  handleHomeTouchEnd(dragRefs, homeMealIndex, goToHomeViewerMeal, setDragOffset);
}

function handleMouseDown(e) {
  handleHomeMouseDown(e, dragRefs);
}

function handleMouseMove(e) {
  handleHomeMouseMove(e, dragRefs, setDragOffset);
}

function handleMouseUp() {
  handleHomeMouseUp(dragRefs, homeMealIndex, goToHomeViewerMeal, setDragOffset);
}

const cardStyle = {
  background: appTheme.cardBackground,
  border: appTheme.cardBorder,
  borderRadius: "24px",
  padding: isMobile ? "16px" : "18px",
  marginBottom: "16px",
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  transition:
    "background 0.25s ease, border 0.25s ease, box-shadow 0.25s ease"
};

const planOverviewStyle = {
  border: "1px solid rgba(255, 255, 255, 0.42)",
  borderRadius: isMobile ? "22px" : "26px",
  padding: isMobile ? "16px" : "20px",
  marginBottom: "18px",
  background: appTheme.planBackground,
  backdropFilter: "blur(18px) saturate(170%)",
  WebkitBackdropFilter: "blur(18px) saturate(170%)",
  boxShadow:
    "0 14px 36px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
  position: "relative",
  zIndex: 10,
  overflow: "visible"
};

function shuffleWeek() {
  const mealTypes = ["breakfast", "lunch", "dinner"];
  const weekDays = getPlanCalendarDays(planCalendarWeekOffset);

  setPlan((prevPlan) => {
    const updatedPlan = { ...prevPlan };

    weekDays.forEach((date) => {
      const dateKey = getDateKey(date);
      updatedPlan[dateKey] = {};

      mealTypes.forEach((mealType) => {
        const filtered = recipes.filter((recipe) =>
          recipe.categories.includes(mealType)
        );
        if (filtered.length === 0) return;
        const random = filtered[Math.floor(Math.random() * filtered.length)];
        updatedPlan[dateKey][mealType] = random;
      });
    });

    return updatedPlan;
  });

  setPlanToastMessage("Week shuffled with new recipes!");
}

const filteredProgrammes = programmeLibrary.filter((programme) => {
  const query = programmeSearch.toLowerCase();

  return (
    programme.name.toLowerCase().includes(query) ||
    programme.focus.toLowerCase().includes(query) ||
    programme.summary.toLowerCase().includes(query)
  );
});

  // Public pages — accessible without auth, no session dependency
  const pathname = window.location.pathname;
  if (pathname === "/terms") return <TermsPage />;
  if (pathname === "/privacy") return <PrivacyPage />;

  // Still checking whether a session exists — show splash if active, cream screen otherwise
  if (session === undefined) {
    if (showSplash) return <SplashScreen onComplete={handleSplashComplete} />;
    return <div style={{ minHeight: "100vh", background: appTheme.pageBackground }} />;
  }

  // Not signed in — show onboarding flow (default) or sign-in screen
  if (session === null) {
    return (
      <>
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
        <OfflineScreen />
        {authMode === "signin"
          ? <AuthScreen onGoToOnboarding={() => { setPendingQuizData(null); setAuthMode("onboarding"); }} />
          : <OnboardingFlow
              onGoToSignIn={(quizData) => {
                setPendingQuizData(quizData ?? null);
                setAuthMode("signin");
              }}
            />
        }
      </>
    );
  }

  // Password recovery — user arrived via a reset-password email link
  if (showPasswordReset) {
    return (
      <>
        <OfflineScreen />
        <PasswordResetScreen
          appTheme={appTheme}
          onComplete={() => setShowPasswordReset(false)}
        />
      </>
    );
  }

  // Edge case: signed in but onboarding was never completed (e.g. interrupted signup)
  if (!hasCompletedOnboarding) {
    return (
      <>
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
        <OfflineScreen />
        <OnboardingFlow
          existingSession={session}
          onComplete={() => setHasCompletedOnboarding(true)}
          onGoToSignIn={() => {}}
        />
      </>
    );
  }

  // Signed in after completing the onboarding quiz — offer to update their profile.
  // Guard requires both data and targets to be present; a stale or malformed value falls through.
  if (pendingQuizData?.data && pendingQuizData?.targets) {
    return (
      <>
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
        <OfflineScreen />
        <PendingUpdatePrompt
          pendingQuizData={pendingQuizData}
          session={session}
          onComplete={(applied) => {
            if (applied) {
              const { data: qd, targets: qt } = pendingQuizData;
              if (qd?.name) setProfileName(qd.name);
              setDataHub(prev => ({
                ...prev,
                gender:        qd?.gender        || prev.gender,
                dob:           qd?.dob           || prev.dob,
                currentWeight: qd?.currentWeight || prev.currentWeight,
                height:        qd?.height        || prev.height,
                activityLevel: qd?.activityLevel || prev.activityLevel,
                goal:          qd?.goal          || prev.goal,
              }));
              if (qt?.calories != null) {
                setNutritionTargets({
                  calories: qt.calories,
                  protein:  qt.protein,
                  carbs:    qt.carbs,
                  fats:     qt.fats,
                });
              }
            }
            setPendingQuizData(null);
          }}
        />
      </>
    );
  }

  return (
  <>
  {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
  <OfflineScreen />
  <style>
  {modalAnimationStyle}
  {globalHelperStyles}
</style>

<div
  style={{
    padding: isMobile ? "18px 14px 20px" : "22px 20px 24px",
    fontFamily: appTheme.fontBody,
    maxWidth: "980px",
    margin: "0 auto",
    background: appTheme.pageBackground,
    minHeight: "100vh",
    color: appTheme.primaryText,
    transition: "background 0.25s ease, color 0.25s ease"
  }}
>

<div
  ref={tabContentRef}
  style={{
    flex: 1,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch"
  }}
>
{activeTab === "home" && (
  <HomeTab
    isLoading={savedMealsLoading}
    isMobile={isMobile}
    appTheme={appTheme}
    profileName={profileName}
    today={today}
    plan={plan}
    completedMeals={completedMeals}
    hoveredButton={hoveredButton}
    setHoveredButton={setHoveredButton}
    getMealCardBackground={getMealCardBackground}
    completeMeal={completeMeal}
    setActiveTab={setActiveTab}
    openRecipeDetails={openRecipeDetails}
    getDateKey={getDateKey}
    nutritionTargets={nutritionTargets}
    currentStreak={currentStreak}
    profileCalendarMonthLabel={profileCalendarMonthLabel}
    changeProfileCalendarMonth={handleChangeProfileCalendarMonth}
    profileCalendarCells={profileCalendarCells}
    getProfileCalendarDateKey={getProfileCalendarDateKey}
    todayDate={todayDate}
    profileCalendarYear={profileCalendarYear}
    profileCalendarMonth={profileCalendarMonth}
    hasCompletedMealOnDate={checkDateCompleted}
    openQuickAddModal={() => openManualEntryModal(getDateKey(new Date()))}
    removeSnack={removeSnack}
    openCookingMode={openCookingMode}
    openEditSnackModal={openEditSnackModal}
    onOpenSettings={() => setShowSettingsModal(true)}
    profileImage={profileImage}
    planningMode={planningMode}
    selectedProgramme={selectedProgramme}
    currentProgrammeWeekIndex={currentProgrammeWeekIndex}
    currentProgrammeWeek={currentProgrammeWeek}
    recipes={recipes}
  />
)}

{activeTab === "plan" && (
  <PlanTab
    isLoading={savedMealsLoading}
    planOverviewStyle={planOverviewStyle}
    planningMode={planningMode}
    setPlanningMode={setPlanningMode}
    isMobile={isMobile}
    formatWeekBeginning={formatWeekBeginning}
    planCalendarWeekOffset={planCalendarWeekOffset}
    getMealsPlannedThisWeek={getMealsPlannedThisWeek}
    generateWeek={generateWeek}
    appTheme={appTheme}
    getDateKey={getDateKey}
    openMealSlotSheet={openMealSlotSheet}
    openMealChoice={openMealChoice}
    openProgrammePicker={openProgrammePicker}
    shuffleWeek={shuffleWeek}
    setShowClearPlanConfirm={setShowClearPlanConfirm}
    setShowRemoveAllWeeksConfirm={setShowRemoveAllWeeksConfirm}
    secondaryButtonStyle={secondaryButtonStyle}
    showClearPlanConfirm={showClearPlanConfirm}
    clearEntirePlan={clearEntirePlan}
    showRemoveAllWeeksConfirm={showRemoveAllWeeksConfirm}
    removeAllWeeks={removeAllWeeks}
    hoveredButton={hoveredButton}
    setHoveredButton={setHoveredButton}
    setDailyNotes={saveDailyNote}
    setPlanCalendarWeekOffset={setPlanCalendarWeekOffset}
    getPlanCalendarDays={getPlanCalendarDays}
    setSelectedPlanDay={setSelectedPlanDay}
    selectedPlanDay={selectedPlanDay}
    plan={plan}
    completedMeals={completedMeals}
    removeFromPlan={removeFromPlan}
    smallSecondaryButtonStyle={smallSecondaryButtonStyle}
    completeMeal={completeMeal}
    expandedMacroDays={expandedMacroDays}
    toggleMacroDay={toggleMacroDay}
    cardStyle={cardStyle}
    dailyNotes={dailyNotes}
    filteredProgrammes={filteredProgrammes}
    openProgrammeDetails={openProgrammeDetails}
    chooseProgramme={chooseProgramme}
    selectedProgramme={selectedProgramme}
    viewSelectedProgramme={viewSelectedProgramme}
    setActiveTab={setActiveTab}
    weekTransitionDirection={weekTransitionDirection}
    currentProgrammeWeekIndex={currentProgrammeWeekIndex}
    getProgrammeWeekCount={getProgrammeWeekCount}
    switchProgrammeWeek={switchProgrammeWeek}
    currentProgrammeProgress={currentProgrammeProgress}
    goToProgrammeWeek={goToProgrammeWeek}
    currentProgrammeWeek={currentProgrammeWeek}
    quitProgramme={quitProgramme}
    clearSelectedProgramme={() => setSelectedProgramme(null)}
    restartProgramme={restartProgramme}
    days={days}
    today={today}
    getRecipeByName={getRecipeByName}
    openProgrammeRecipe={openProgrammeRecipe}
    startProgrammeMealSwap={startProgrammeMealSwap}
    showFullWeek={showFullWeek}
    setShowFullWeek={setShowFullWeek}
    openManualEntryModal={openManualEntryModal}
    removeSnack={removeSnack}
    favouriteRecipes={favouriteRecipes}
    toggleFavourite={toggleFavourite}
    openRecipeDetails={openRecipeDetails}
    hasCreatedManualPlan={hasCreatedManualPlan}
    hasChosenProgramme={hasChosenProgramme}
    onCreateManualPlan={handleCreateManualPlan}
    onChooseProgramme={handleChooseProgrammeFlag}
  />
)}

{showFullWeek && (
  <FullWeekView
    onClose={() => setShowFullWeek(false)}
    onSelectDay={(dateKey) => { setSelectedPlanDay(dateKey); setShowFullWeek(false); }}
    appTheme={appTheme}
    isMobile={isMobile}
    planningMode={planningMode}
    plan={plan}
    getDateKey={getDateKey}
    getPlanCalendarDays={getPlanCalendarDays}
    planCalendarWeekOffset={planCalendarWeekOffset}
    currentProgrammeWeek={currentProgrammeWeek}
    currentProgrammeWeekIndex={currentProgrammeWeekIndex}
    days={days}
    completedMeals={completedMeals}
    selectedProgramme={selectedProgramme}
    goToProgrammeWeek={goToProgrammeWeek}
    getProgrammeWeekCount={getProgrammeWeekCount}
  />
)}

<ProgrammeSuccessModal
  isOpen={showProgrammeSuccessModal}
  programme={selectedProgramme}
  appTheme={appTheme}
  isMobile={isMobile}
  onChooseNew={() => {
    const completedAt = new Date().toISOString();
    setCompletedProgrammes(prev => [...prev, { ...selectedProgramme, completedAt }]);
    const userId = session?.user?.id;
    if (userId && selectedProgramme) {
      supabase.from("completed_programmes").insert({
        user_id: userId,
        programme_id: String(selectedProgramme.id || selectedProgramme.name),
        programme_name: selectedProgramme.name,
        completed_at: completedAt,
      }).then(({ error }) => { if (error) console.error("completedProgrammes insert:", error); });
    }
    setShowProgrammeSuccessModal(false);
    setSelectedProgramme(null);
    setPlanningMode("programme");
  }}
  onBackToManual={() => {
    const completedAt = new Date().toISOString();
    setCompletedProgrammes(prev => [...prev, { ...selectedProgramme, completedAt }]);
    const userId = session?.user?.id;
    if (userId && selectedProgramme) {
      supabase.from("completed_programmes").insert({
        user_id: userId,
        programme_id: String(selectedProgramme.id || selectedProgramme.name),
        programme_name: selectedProgramme.name,
        completed_at: completedAt,
      }).then(({ error }) => { if (error) console.error("completedProgrammes insert:", error); });
    }
    setShowProgrammeSuccessModal(false);
    setSelectedProgramme(null);
    setPlanningMode("manual");
  }}
/>

{activeTab === "recipes" && (
  <RecipesTab
    setHasTriedToSaveAddRecipe={setHasTriedToSaveAddRecipe}
    secondaryButtonStyle={secondaryButtonStyle}
    recipeSearch={recipeSearch}
    setRecipeSearch={setRecipeSearch}
    sortBy={sortBy}
    setSortBy={setSortBy}
    hoveredButton={hoveredButton}
    setHoveredButton={setHoveredButton}
    activeFilters={activeFilters}
    applyFilters={applyFilters}
    clearAllFilters={clearAllFilters}
    recipeFilters={recipeFilters}
    allRecipeFilters={allRecipeFilters}
    toggleFilter={toggleFilter}
    recipes={recipes}
    showMoreFilters={showMoreFilters}
    setShowMoreFilters={setShowMoreFilters}
    recipesTabFilteredRecipes={recipesTabFilteredRecipes}
    setAddRecipeWarning={setAddRecipeWarning}
    setShowAddRecipeForm={setShowAddRecipeForm}
    showAddRecipeForm={showAddRecipeForm}
    modalOverlayStyle={overlayStyle}
    modalContentStyle={contentStyle}
    clearAddRecipeForm={clearAddRecipeForm}
    isMobile={isMobile}
    hasTriedToSaveAddRecipe={hasTriedToSaveAddRecipe}
    isNewRecipeNameFilled={isNewRecipeNameFilled}
    newRecipeName={newRecipeName}
    setNewRecipeName={setNewRecipeName}
    newRecipeImage={newRecipeImage}
    setNewRecipeImage={setNewRecipeImage}
    newRecipeCategory={newRecipeCategory}
    newRecipeIngredients={newRecipeIngredients}
    newRecipeDescription={newRecipeDescription}
    setNewRecipeDescription={setNewRecipeDescription}
    newRecipeTime={newRecipeTime}
    setNewRecipeTime={setNewRecipeTime}
    newRecipeCalories={newRecipeCalories}
    setNewRecipeCalories={setNewRecipeCalories}
    newRecipeServings={newRecipeServings}
    setNewRecipeServings={setNewRecipeServings}
    setNewRecipeIngredients={setNewRecipeIngredients}
    isNewRecipeIngredientsFilled={isNewRecipeIngredientsFilled}
    setNewRecipeCategory={setNewRecipeCategory}
    isNewRecipeCategoryFilled={isNewRecipeCategoryFilled}
    newRecipeTag={newRecipeTag}
    setNewRecipeTag={setNewRecipeTag}
    newRecipeProtein={newRecipeProtein}
    setNewRecipeProtein={setNewRecipeProtein}
    newRecipeCarbs={newRecipeCarbs}
    setNewRecipeCarbs={setNewRecipeCarbs}
    newRecipeFats={newRecipeFats}
    setNewRecipeFats={setNewRecipeFats}
    newRecipeInstructions={newRecipeInstructions}
    setNewRecipeInstructions={setNewRecipeInstructions}
    activeNewRecipeSections={activeNewRecipeSections}
    setActiveNewRecipeSections={setActiveNewRecipeSections}
    isNewRecipeCaloriesFilled={isNewRecipeCaloriesFilled}
    isNewRecipeProteinFilled={isNewRecipeProteinFilled}
    isNewRecipeCarbsFilled={isNewRecipeCarbsFilled}
    isNewRecipeFatsFilled={isNewRecipeFatsFilled}
    addRecipeWarning={addRecipeWarning}
    isAddRecipeValid={isAddRecipeValid}
    addRecipe={addRecipe}
    recipeGridCard={(recipe, opts) => (
  <RecipeGridCard
    recipe={recipe}
    isMobile={isMobile}
    hoveredButton={hoveredButton}
    setHoveredButton={setHoveredButton}
    openRecipeDetails={openRecipeDetails}
    openRecipeQuickAdd={openRecipeQuickAdd}
    toggleFavourite={toggleFavourite}
    favouriteRecipes={favouriteRecipes}
    appTheme={appTheme}
    hideCustomBadge={opts?.hideCustomBadge}
  />
)}
    closeButtonStyle={closeButtonStyle}
    appTheme={appTheme}
    favouriteRecipes={favouriteRecipes}
    recipesShowFavouritesOnly={recipesShowFavouritesOnly}
    setRecipesShowFavouritesOnly={setRecipesShowFavouritesOnly}
    recipesShowMyRecipesOnly={recipesShowMyRecipesOnly}
    setRecipesShowMyRecipesOnly={setRecipesShowMyRecipesOnly}
    openRecipeDetails={openRecipeDetails}
    toggleFavourite={toggleFavourite}
  />
)}

{activeTab === "shopping" && (
  <ShoppingTab
  shoppingList={shoppingList}
  checkedItems={checkedItems}
  setCheckedItems={setCheckedItems}
  newItem={newItem}
  setNewItem={setNewItem}
  addManualItem={addManualItem}
  cardStyle={cardStyle}
  appTheme={appTheme}
  setActiveTab={setActiveTab}
  groupedShoppingList={groupedShoppingList}
  isMobile={isMobile}
  toggleItem={toggleItem}
  manualItems={manualItems}
  removeManualItem={removeManualItem}
  stapleItems={stapleItems}
  hiddenStaples={hiddenStaples}
  setHiddenStaples={setHiddenStaples}
  customStaples={customStaples}
  setCustomStaples={setCustomStaples}
  plan={plan}
  pantryItems={pantryItems}
  setPantryItems={setPantryItems}
  addNamedManualItem={addNamedManualItem}
  removedDefaultStaples={removedDefaultStaples}
  setRemovedDefaultStaples={setRemovedDefaultStaples}
/>
)}

</div>

<AddWeeksModal
  isOpen={showAddWeeksModal}
  onClose={() => setShowAddWeeksModal(false)}
  isMobile={isMobile}
  hoveredButton={hoveredButton}
  setHoveredButton={setHoveredButton}
  handleAddWeeks={handleAddWeeks}
  modalOverlayStyle={overlayStyle}
  modalContentStyle={contentStyle}
  closeButtonStyle={closeButtonStyle}
  secondaryButtonStyle={secondaryButtonStyle}
  appTheme={appTheme}
/>

<ProgrammePickerModal
  isOpen={showProgrammePicker}
  onClose={() => setShowProgrammePicker(false)}
  isMobile={isMobile}
  hoveredButton={hoveredButton}
  setHoveredButton={setHoveredButton}
  programmeSearch={programmeSearch}
  setProgrammeSearch={setProgrammeSearch}
  filteredProgrammes={filteredProgrammes}
  openProgrammeDetails={openProgrammeDetails}
  chooseProgramme={chooseProgramme}
  modalOverlayStyle={overlayStyle}
  modalContentStyle={contentStyle}
  closeButtonStyle={closeButtonStyle}
  secondaryButtonStyle={secondaryButtonStyle}
  appTheme={appTheme}
/>

<ProgrammeDetailsModal
  isOpen={!!selectedProgrammeDetail}
  isActive={selectedProgramme?.id === selectedProgrammeDetail?.id}
  selectedProgrammeDetail={selectedProgrammeDetail}
  isMobile={isMobile}
  closeProgrammeDetails={closeProgrammeDetails}
  hoveredButton={hoveredButton}
  setHoveredButton={setHoveredButton}
  secondaryButtonStyle={secondaryButtonStyle}
  chooseProgramme={chooseProgramme}
  appTheme={appTheme}
/>

<EditRecipeModal
  editingRecipeName={editingRecipeName}
  editRecipeImage={editRecipeImage}
  setEditRecipeImage={setEditRecipeImage}
  modalOverlayStyle={overlayStyle}
  modalContentStyle={contentStyle}
  cancelEditingRecipe={cancelEditingRecipe}
  handleDeleteSelectedRecipe={handleDeleteSelectedRecipe}
  hoveredButton={hoveredButton}
  setHoveredButton={setHoveredButton}
  closeButtonStyle={closeButtonStyle}
  editRecipeName={editRecipeName}
  setEditRecipeName={setEditRecipeName}
  editRecipeCategory={editRecipeCategory}
  editRecipeIngredients={editRecipeIngredients}
  setEditRecipeWarning={setEditRecipeWarning}
  hasTriedToSaveEditRecipe={hasTriedToSaveEditRecipe}
  isEditRecipeNameFilled={isEditRecipeNameFilled}
  editRecipeDescription={editRecipeDescription}
  setEditRecipeDescription={setEditRecipeDescription}
  editRecipeTime={editRecipeTime}
  setEditRecipeTime={setEditRecipeTime}
  editRecipeCalories={editRecipeCalories}
  setEditRecipeCalories={setEditRecipeCalories}
  editRecipeServings={editRecipeServings}
  setEditRecipeServings={setEditRecipeServings}
  setEditRecipeIngredients={setEditRecipeIngredients}
  isEditRecipeIngredientsFilled={isEditRecipeIngredientsFilled}
  isEditRecipeCaloriesFilled={isEditRecipeCaloriesFilled}
  isEditRecipeProteinFilled={isEditRecipeProteinFilled}
  isEditRecipeCarbsFilled={isEditRecipeCarbsFilled}
  isEditRecipeFatsFilled={isEditRecipeFatsFilled}
  setEditRecipeCategory={setEditRecipeCategory}
  isEditRecipeCategoryFilled={isEditRecipeCategoryFilled}
  editRecipeTag={editRecipeTag}
  setEditRecipeTag={setEditRecipeTag}
  editRecipeProtein={editRecipeProtein}
  setEditRecipeProtein={setEditRecipeProtein}
  editRecipeCarbs={editRecipeCarbs}
  setEditRecipeCarbs={setEditRecipeCarbs}
  editRecipeFats={editRecipeFats}
  setEditRecipeFats={setEditRecipeFats}
  editRecipeInstructions={editRecipeInstructions}
  setEditRecipeInstructions={setEditRecipeInstructions}
  activeEditRecipeSections={activeEditRecipeSections}
  setActiveEditRecipeSections={setActiveEditRecipeSections}
  editRecipeWarning={editRecipeWarning}
  setHasTriedToSaveEditRecipe={setHasTriedToSaveEditRecipe}
  isEditRecipeValid={isEditRecipeValid}
  saveEditedRecipe={saveEditedRecipe}
  primaryButtonStyle={primaryButtonStyle}
  isMobile={isMobile}
  appTheme= {appTheme}
/>

<RecipeDetailsModal
  isOpen={isRecipeDetailsModalOpen}
  allowQuickAdd={activeTab === "recipes"}
  fullPage={isFullPageRecipe}
  fullPageContext={recipeFullPageContext}
  modalOverlayStyle={overlayStyle}
  closeRecipeModal={closeRecipeModal}
  modalContentStyle={contentStyle}
  isMobile={isMobile}
  selectedRecipe={selectedRecipe}
  closeButtonStyle={closeButtonStyle}
  hoveredButton={hoveredButton}
  setHoveredButton={setHoveredButton}
  selectedPlanSlots={selectedPlanSlots}
  programmeMealEditTarget={programmeMealEditTarget}
  mealSlotSheetTarget={mealSlotSheetTarget}
  applyRecipeToProgrammeMeal={applyRecipeToProgrammeMeal}
  applyMealToSlot={applyMealToSlot}
  addRecipeToPlanSelectedSlots={addRecipeToPlanSelectedSlots}
  goToRecipePlanView={goToRecipePlanView}
  secondaryButtonStyle={secondaryButtonStyle}
  handleEditSelectedRecipe={handleEditSelectedRecipe}
  handleDeleteSelectedRecipe={handleDeleteSelectedRecipe}
  recipeModalView={recipeModalView}
  recipeModalDirection={recipeModalDirection}
  goToRecipeDetailsView={goToRecipeDetailsView}
  days={days}
  plan={plan}
  toggleRecipePlanSlot={toggleRecipePlanSlot}
  confirmAddSelectedRecipeToPlan={confirmAddSelectedRecipeToPlan}
  recipeNotes={recipeNotes}
  setRecipeNotes={saveRecipeNote}
  primaryButtonStyle={primaryButtonStyle}
  appTheme={appTheme}
  setPlanToastMessage={setPlanToastMessage}
  setPlan={setPlan}
  getDateKey={getDateKey}
  getPlanCalendarDays={getPlanCalendarDays}
  planCalendarWeekOffset={planCalendarWeekOffset}
  completeMeal={completeMeal}
  completedMeals={completedMeals}
  favouriteRecipes={favouriteRecipes}
  toggleFavourite={toggleFavourite}
  openMealSlotSheet={openMealSlotSheet}
  removeFromPlan={removeFromPlan}
  openManualEntryModal={openManualEntryModal}
  servingSizePreference={servingSizePreference}
  onStartCooking={() => {
    const ctx = recipeFullPageContext?.dateKey && recipeFullPageContext?.mealType
      ? { dateKey: recipeFullPageContext.dateKey, mealType: recipeFullPageContext.mealType }
      : null;
    openCookingMode(selectedRecipe, ctx);
  }}
/>

<MealChoiceSheet
  show={showMealChoiceSheet}
  target={mealChoiceTarget}
  onClose={closeMealChoiceSheet}
  onBrowseRecipes={() => {
    closeMealChoiceSheet();
    if (mealChoiceTarget) openMealSlotSheet(mealChoiceTarget.dateKey, mealChoiceTarget.mealType);
  }}
  onAddMeal={(meal) => {
    if (!mealChoiceTarget) return;
    const { dateKey, mealType } = mealChoiceTarget;
    setPlan(prev => ({ ...prev, [dateKey]: { ...(prev[dateKey] || {}), [mealType]: meal } }));
    closeMealChoiceSheet();
  }}
  savedManualMeals={savedManualMeals}
  savedMealsLoading={savedMealsLoading}
  onSaveMeal={addSavedMeal}
  onDeleteSavedMeal={deleteSavedMeal}
  isMobile={isMobile}
  appTheme={appTheme}
/>

<MealSlotSheet
  showMealSlotSheet={showMealSlotSheet}
  closeMealSlotSheet={closeMealSlotSheet}
  isMobile={isMobile}
  appTheme={appTheme}
  mealSheetExpanded={mealSheetExpanded}
  isClosingMealSlotSheet={isClosingMealSlotSheet}
  mealSheetIsDragging={mealSheetIsDragging}
  mealSheetDragY={mealSheetDragY}
  handleSheetDragStart={handleSheetDragStart}
  handleSheetDragMove={handleSheetDragMove}
  handleSheetDragEnd={handleSheetDragEnd}
  mealSlotSheetTarget={mealSlotSheetTarget}
  plan={plan}
  removeFromPlan={removeFromPlan}
  setMealSlotSearch={setMealSlotSearch}
  mealSlotSearch={mealSlotSearch}
  setShowFavouritesOnly={setShowFavouritesOnly}
  showFavouritesOnly={showFavouritesOnly}
  recipes={recipes}
  favouriteRecipes={favouriteRecipes}
  mealSlotSelectedRecipe={mealSlotSelectedRecipe}
  setMealSlotSelectedRecipe={setMealSlotSelectedRecipe}
  openRecipeDetails={openRecipeDetails}
  applyMealToSlot={applyMealToSlot}
/>

<ManualEntryModal
  isOpen={showManualEntryModal}
  onClose={() => {
    setShowManualEntryModal(false);
    setManualEntryEditingSnackId(null);
    setManualEntryInitialValues(null);
  }}
  onSave={handleSaveSnack}
  appTheme={appTheme}
  isMobile={isMobile}
  mode={manualEntryMode}
  initialValues={manualEntryInitialValues}
  isEditing={!!manualEntryEditingSnackId}
/>

<HomeDayModal
  setDragOffset={setDragOffset}
  isDraggingRef={isDraggingRef}
  isOpen={isHomeDayModalOpen}
  modalOverlayStyle={overlayStyle}
  modalContentStyle={contentStyle}
  selectedHomeDay={selectedHomeDay}
  orderedHomeMeals={orderedHomeMeals}
  homeMealIndex={homeMealIndex}
  dragOffset={dragOffset}
  closeRecipeModal={closeRecipeModal}
  handleTouchStart={handleTouchStart}
  handleTouchMove={handleTouchMove}
  handleTouchEnd={handleTouchEnd}
  handleMouseDown={handleMouseDown}
  handleMouseMove={handleMouseMove}
  handleMouseUp={handleMouseUp}
  closeButtonStyle={closeButtonStyle}
  hoveredButton={hoveredButton}
  setHoveredButton={setHoveredButton}
  activeModalRecipe={activeModalRecipe}
  activeModalMealType={activeModalMealType}
  appTheme={appTheme}
  isCompletedMeal={(item) => item.isCompleted}
  completeMeal={completeMeal}
  completedMeals={completedMeals}
  selectedPlanDay={selectedPlanDay}
  getDateKey={getDateKey}
  isMobile={isMobile}
  secondaryButtonStyle={secondaryButtonStyle}
  primaryButtonStyle={primaryButtonStyle}
  goToHomeViewerMeal={goToHomeViewerMeal}
  handleChooseRecipeFromHomeModal={handleChooseRecipeFromHomeModal}
  handleQuickAddFromHomeModal={handleQuickAddFromHomeModal}
  setActiveTab={setActiveTab}
  setRecipeSearch={setRecipeSearch}
  setSelectedRecipe={setSelectedRecipe}
  setSelectedHomeDay={setSelectedHomeDay}
  setHomeMealIndex={setHomeMealIndex}
/>

<MealTimePickerModal
  activeMealTimePicker={activeMealTimePicker}
  setActiveMealTimePicker={setActiveMealTimePicker}
  modalOverlayStyle={overlayStyle}
  isMobile={isMobile}
  appTheme={appTheme}
  saveMealTime={saveMealTime}
  tempHour={tempHour}
  setTempHour={setTempHour}
  tempMinute={tempMinute}
  setTempMinute={setTempMinute}
  tempPeriod={tempPeriod}
  setTempPeriod={setTempPeriod}
/>

<PlanToast
  planToastMessage={planToastMessage}
  isMobile={isMobile}
  lowPosition={showSettingsModal}
/>

<SettingsModal
  showSettingsModal={showSettingsModal}
  closeSettingsModal={closeSettingsModal}
  isClosingSettingsModal={isClosingSettingsModal}
  handleSettingsSwipeTouchStart={handleSettingsSwipeTouchStart}
  handleSettingsSwipeTouchMove={handleSettingsSwipeTouchMove}
  handleSettingsSwipeTouchEnd={handleSettingsSwipeTouchEnd}
  modalOverlayStyle={overlayStyle}
  modalContentStyle={contentStyle}
  isMobile={isMobile}
  appTheme={appTheme}
  settingsPage={settingsPage}
  settingsPageDirection={settingsPageDirection}
  goBackSettingsPage={goBackSettingsPage}
  getSettingsPageTitle={() => getSettingsPageTitle(settingsPage)}
  renderSettingsRow={(label, type, isDanger, onClick) => renderSettingsRow(appTheme, label, type, isDanger, onClick)}
  renderSettingsInfoRow={(label, value) => renderSettingsInfoRow(appTheme, label, value)}
  renderSettingsToggleRow={(label, value, onToggle) => renderSettingsToggleRow(appTheme, label, value, onToggle)}
  renderSettingsIcon={renderSettingsIcon}
  renderSettingsProfileCard={() => (
  <SettingsProfileCard
    profileImage={profileImage}
    profileName={profileName}
    profileEmail={profileEmail}
    appTheme={appTheme}
  />
)}
  openSettingsPage={openSettingsPage}
  setActiveTab={setActiveTab}
  profileImage={profileImage}
  profileName={profileName}
  profileEmail={profileEmail}
  isEditingAccount={isEditingAccount}
  editName={editName}
  setEditName={setEditName}
  editEmail={editEmail}
  setEditEmail={setEditEmail}
  editNameWarning={editNameWarning}
  setEditNameWarning={setEditNameWarning}
  setIsEditingAccount={setIsEditingAccount}
  setProfileName={setProfileName}
  setProfileEmail={setProfileEmail}
  defaultServings={defaultServings}
  setDefaultServings={setDefaultServings}
  dietaryPreference={dietaryPreference}
  setDietaryPreference={setAndSyncDietaryPreference}
  measurementUnit={measurementUnit}
  setMeasurementUnit={setMeasurementUnit}
  showDeleteConfirm={showDeleteConfirm}
  setShowDeleteConfirm={setShowDeleteConfirm}
  deleteConfirmText={deleteConfirmText}
  setDeleteConfirmText={setDeleteConfirmText}
  mealReminders={mealReminders}
  setMealReminders={setMealReminders}
  breakfastTime={breakfastTime}
  lunchTime={lunchTime}
  dinnerTime={dinnerTime}
  openMealTimePicker={openMealTimePicker}
  planReminders={planReminders}
  setPlanReminders={setPlanReminders}
  programmeReminders={programmeReminders}
  setProgrammeReminders={setProgrammeReminders}
  appUpdates={appUpdates}
  setAppUpdates={setAppUpdates}
  recipes={recipes}
  plan={plan}
  currentStreak={currentStreak}
  showClearDataConfirm={showClearDataConfirm}
  setShowClearDataConfirm={setShowClearDataConfirm}
  supportMessages={supportMessages}
  supportInput={supportInput}
  setSupportInput={setSupportInput}
  supportIsTyping={supportIsTyping}
  supportError={supportError}
  sendSupportMessage={sendSupportMessage}
  setPlanToastMessage={setPlanToastMessage}
  expandedFaqs={expandedFaqs}
  setExpandedFaqs={setExpandedFaqs}
  setSettingsPageDirection={setSettingsPageDirection}
  setSettingsPage={setSettingsPage}
  FeedbackPage={FeedbackPage}
  dataHub={dataHub}
  setDataHub={setDataHub}
  nutritionTargets={nutritionTargets}
  setNutritionTargets={setNutritionTargets}
  calculateNutritionTargets={calculateNutritionTargets}
  userId={session?.user?.id}
  handleProfileImageChange={handleProfileImageChange}
  removeProfileImage={removeProfileImage}
  onSaveProfileImage={saveProfileImage}
/>

{showDeleteRecipeConfirm && (
  <>
    <div onClick={closeDeleteRecipeConfirm} style={{ position: "fixed", inset: 0, zIndex: 99998, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)" }} />
    <div onClick={e => e.stopPropagation()} style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 99999, background: appTheme.pageBackground, borderRadius: "24px 24px 0 0", animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)" }}>
      <div style={{ padding: "32px 24px 40px", display: "flex", flexDirection: "column", gap: "0" }}>
        <div style={{ fontFamily: appTheme.fontHeading, fontSize: "22px", fontWeight: "500", color: appTheme.primaryText, marginBottom: "8px" }}>
          Delete recipe?
        </div>
        <div style={{ fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.5, marginBottom: "24px" }}>
          This will permanently remove the recipe from your library. This cannot be undone.
        </div>
        <div style={{ display: "grid", gap: "10px" }}>
          <button onClick={confirmDeleteSelectedRecipe} style={{ width: "100%", padding: "15px", borderRadius: "14px", border: "none", background: "#DC2626", color: "#fff", fontFamily: appTheme.fontBody, fontSize: "15px", fontWeight: "700", cursor: "pointer" }}>
            Delete
          </button>
          <button onClick={closeDeleteRecipeConfirm} style={{ width: "100%", padding: "15px", borderRadius: "14px", border: appTheme.cardBorder, background: appTheme.cardBackground, fontFamily: appTheme.fontBody, fontSize: "15px", fontWeight: "600", color: appTheme.primaryText, cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  </>
)}

{cookingModeRecipe && (
  <CookingMode
    recipe={cookingModeRecipe}
    isMobile={isMobile}
    appTheme={appTheme}
    onClose={closeCookingMode}
    onMarkCooked={
      cookingModeContext?.dateKey && cookingModeContext?.mealType
        ? () => completeMeal(cookingModeContext.dateKey, cookingModeContext.mealType)
        : null
    }
    existingNote={recipeNotes?.[cookingModeRecipe?.name] || ""}
    onSaveNote={(text) => saveRecipeNote(cookingModeRecipe.name, text)}
  />
)}

<BottomNav
  isAnyModalOpen={isAnyModalOpen}
  isMobile={isMobile}
  appTheme={appTheme}
  activeTab={activeTab}
  hoveredButton={hoveredButton}
  setHoveredButton={setHoveredButton}
  setActiveTab={setActiveTab}
/>

    </div>
  </>
  );
}

export default App;